/* eslint-disable max-lines */
import * as Bowser from 'bowser'
import { applicationFieldChanged } from 'common/actions/application'
import { brandFromHost, consumerApiUrl, fionaBaseApiUrl, releaseVersion } from 'embed/constants/config'
import {
  ApplicationStep,
  BUSINESS_INSIDER_COMPANY_UUID,
  CoApplicantAddressMatch,
  CommonDispatch,
  DEFAULT_REDEMPTION_TYPE_VALUES,
  DefaultCreditCardFlow,
  DepositAmountIntentRange,
  DEPOSITS_AMOUNT_INTENT_RANGE_FILTER_LIST,
  FieldChangedOptions,
  FieldDataSources,
  FieldDataValue,
  FieldsChangedOptions,
  FlowQueryParamForProduct,
  GraduateDegreeTypeOptions,
  isCreditCardDebtCompany,
  isFixedSizeEmbed,
  LifeInsuranceCoverageTier,
  LifeInsuranceQuestionGroups,
  MultiProductDispatch,
  OFFER_URL_KEY,
  PersonalizedStatus,
  PersonalizedStatuses,
  ProductFlow,
  RatesFilter,
  TargetTypes,
  TPH_AMEX_COMPANY_UUID,
  TPH_LIFE_INSURANCE_COMPANY_UUID,
  ViewportUpdateMessageBody,
  Waypoints,
} from 'embed/constants'
import { FinalStateNodeNames } from 'embed/constants/life-insurance'
import Parent from 'common/services/Parent'
import * as Cookies from 'js-cookie'
import { get, snakeCase } from 'lodash'
import { debounce } from 'throttle-debounce'
import * as Sentry from '@sentry/react'

import { fetch } from 'whatwg-fetch'

import {
  captureFetchPreviewOffersError,
  captureGeocoderError,
  captureGeolocateError,
  captureGetRateTablesError,
  captureGetStaticOfferError,
  captureLeadApiError,
  captureRefreshRateTablesError,
  captureSpendingCategoriesRequestError,
  captureZeroOffersWarning,
} from 'embed/constants/errors'

import { CommonState, MultiProductState } from 'embed/reducers'
import { validationDataSelector } from 'embed/selectors'
import {
  configCompanyUuidSelector,
  configCreditCardRewardsEnabledSelector,
  creditFlowSelector,
  isPortalPreviewSelector,
} from 'embed/selectors/config'
import {
  getBool,
  getDateString,
  getDateStringMonthAndYearOnly,
  getFilterOrApp,
  getIntFromString,
  mapRedemptionTypeFromLabelValue,
  urlSerialize,
} from 'embed/utils'
import { getQuestionUuidFromQuestionMachineState, getResponsePathWithSteps } from 'embed/utils/life-insurance'
import {
  CreditOffer,
  DepositsOffer,
  LifeInsuranceOffer,
  LifeInsuranceProductSubType,
  LoanOffer,
  Offer,
  OfferDetail,
  RedemptionType,
  RewardsValueAggregate,
  RewardsValueCalculationRequestBody,
  RewardValueCalculationResponse,
  SpecialOffer,
} from 'embed/constants/models'

import {
  applicationCreditCardRewardsPreferenceValueSelector,
  applicationLifeInsuranceRouterQuestionsMachineConfigSelector,
  applicationStateSelector,
  creditCardApplicationRewardsPreferenceUuidSelector,
  lifeInsuranceApplicationFieldsSelector,
  loanApplicationFieldsSelector,
  loanEmailSelector,
  loanVariantSelector,
} from 'embed/selectors/application'
import {
  filtersCreditAnnualIncomeSelector,
  filtersCreditCardCreditScoreSelector,
  filtersCreditCardPurposesSelector,
  filtersCreditRedemptionTypesSelector,
  filtersCreditSpendingCalculatorSelector,
  filtersLoanCreditScoreSelector,
  filtersLoanPurposeSelector,
  filtersProductSelector,
  filtersVehicleIdTypeSelector,
} from 'embed/selectors/filters'
import { loadingStartTimeSelector } from 'embed/selectors/loading'
import {
  creditCardRatesOffersSelector,
  creditCardRatesUuidSelector,
  lifeInsuranceOfferSelectedSelector,
  lifeInsuranceRatesUuidSelector,
  loanRatesLeadUuidSelector,
  loanRatesUuidSelector,
} from 'embed/selectors/rates'
import Api, { ApiResponse } from 'common/services/Api'
import { getGeoFromIP } from 'common/services/GeolocationService'
import GoogleAnalytics from 'common/services/GoogleAnalytics'
import Geocoder from 'common/services/GoogleMapsGeocoder'
import IpAddress from 'common/services/IpAddress'
import { buildConsumerPartnerPageUrl } from 'common/services/Partnerpage'
import QueryParams from 'common/services/QueryParams'
import Session from 'common/services/Session'
import { ValidationData } from 'common/services/Validation'
import { Fields } from 'embed/constants/fields'
import {
  Brand,
  CitizenshipOptions,
  ClientTags,
  Config,
  ConfigKeys,
  ConfigServiceDataKeys,
  CreditCardFlow,
  CreditCardPurposesOptions,
  CreditCardStep,
  CreditSortOptionUI,
  DepositsStep,
  EducationLevelOptions,
  EmploymentStatusOptions,
  Filter,
  Filters,
  GenderTypeOptions,
  GoogleAnalyticsActions,
  GoogleAnalyticsEventTypes,
  GoogleAnalyticsMetadataFields,
  GoogleAnalyticsMetadataKeys,
  LifeInsuranceAnswer,
  LifeInsuranceQuestion,
  LifeInsuranceStep,
  LoanFlow,
  LoanPurposeOptions,
  LoanStep,
  OfferType,
  ParentData,
  ParentDataKeys,
  PayFrequencyOptions,
  Product,
  ProductApiKeys,
  ProductResponseKeys,
  ProductStep,
  ProductToApiProduct,
  PropertyStatusOptions,
  PublishKey,
  QueryParamKeys,
  RedirectType,
  RewardsPreferenceTypeOptions,
  SortDirection,
  SortOption,
  TrackingKey,
  VehicleIdTypeOptions,
} from 'even-ts-static'

import { TrustedPartner } from 'embed/components/TrustedPartners'
import {
  AVERAGE_EXPENDITURE,
  DEFAULT_SPENDING_CATEGORIES,
  DEFAULT_SPENDING_CATEGORY_PERCENTAGES,
} from 'embed/constants/spendingCalculationAggregates'
import { SpendingCalculatorFields } from 'embed/components/Credit/SpendingCalculator/SpendingProfile/container'
import { LifeInsuranceSimplifiedOffers, RedirectPreferredLook } from 'embed/experiments'
import { uiSelectedCreditStepSelector, uiSelectedStepHistorySelector } from 'embed/selectors/ui'
import { BasicLoan, equals as LoanVariantEquals } from 'ui/multi-product/components/Loan/Application/loan-variant'

const REDIRECT_WAIT = 100
interface LeadCreditCardInformation {
  [Filter.CardPurposes]?: CreditCardPurposesOptions[]
}

interface LeadCreditInformation {
  [Filter.CreditScore]?: string
}

interface LeadEducationInformation {
  [Filter.GraduateDegreeType]?: GraduateDegreeTypeOptions
  [Filter.GraduationDate]?: string
  [Filter.University]?: string
}

interface LeadFinancialInformation {
  [Filter.AnnualIncome]?: number
  [Filter.AvailableAssets]?: number
  [Filter.EmploymentStatus]?: EmploymentStatusOptions
  [Filter.MonthlyHousingPayment]?: number
  [Filter.PayFrequency]?: PayFrequencyOptions
}

interface LeadHealthInformation {
  [Filter.Gender]?: GenderTypeOptions
  [Filter.HeightInInches]?: number
  [Filter.TobaccoSmoker]?: boolean
  [Filter.WeightInPounds]?: number
}

interface LeadLegalInformation {
  [Filter.ConsentsToFCRA]?: boolean
  [Filter.ConsentsToTCPA]?: boolean
  [Filter.TCPALanguage]?: string
}

interface LeadLoanInformation {
  [Filter.LoanAmount]?: number
  [Filter.LoanPurpose]?: LoanPurposeOptions
}

interface LeadVehicleInformation {
  [Filter.EstimatedMileage]?: number
  [Filter.LicensePlateState]?: string
  [Filter.LicensePlateNumber]?: string
  [Filter.MonthlyPayment]?: number
  [Filter.VehicleIdentificationNumber]?: string
}

interface LeadMortgageInformation {
  [Filter.PropertyStatus]?: PropertyStatusOptions
}

interface LeadPersonalInformation {
  [Filter.ActiveMilitary]?: boolean
  [Filter.AddressOne]?: string
  [Filter.AddressTwo]?: string
  [Filter.CitizenshipStatus]?: CitizenshipOptions
  [Filter.City]?: string
  [Filter.CoApplicantInformation]?: LeadCoApplicantPersonalInformation
  [Filter.DateOfBirth]?: string
  [Filter.EducationLevel]?: EducationLevelOptions
  [Filter.Email]?: string
  [Filter.FirstName]?: string
  [Filter.LastName]?: string
  [Filter.PrimaryPhone]?: string
  [Filter.SocialSecurityNumber]?: string
  [Filter.State]?: string
  [Filter.Zipcode]?: string
}

interface LeadCoApplicantPersonalInformation {
  [Filter.StreetAddressOne]?: string
  [Filter.StreetAddressTwo]?: string
  [Filter.AnnualIncome]?: number
  [Filter.City]?: string
  [Filter.DateOfBirth]?: string
  [Filter.FirstName]?: string
  [Filter.LastName]?: string
  [Filter.State]?: string
  [Filter.Zipcode]?: string
}
interface LeadSavingsInformation {
  [Filter.MinDepositAmount]?: number
  [Filter.MaxDepositAmount]?: number
}

interface LeadSessionInformation {
  ipAddress?: string
  userAgent: string
}

interface LeadLifeInsuranceInformation {
  packageTypes?: string[]
  duration?: number
  policyAmount?: number
  carrierUuid?: string
}

interface Lead {
  clientTags: Partial<ClientTags>
  companyUUID?: string
  createdAt?: string
  creditCardInformation: LeadCreditCardInformation
  creditInformation: LeadCreditInformation
  educationInformation: LeadEducationInformation
  financialInformation: LeadFinancialInformation
  healthInformation?: LeadHealthInformation
  isTest?: boolean
  leadProviderName?: string
  legalInformation?: LeadLegalInformation
  loanInformation: LeadLoanInformation
  mortgageInformation?: LeadMortgageInformation
  personalInformation: LeadPersonalInformation
  lifeInsuranceInformation?: LeadLifeInsuranceInformation
  productTypes?: string[]
  productTypesHistory?: { createdAt: string; value: string[] }[]
  referralCompanyUuid?: string
  savingsInformation?: LeadSavingsInformation
  sessionInformation: LeadSessionInformation
  sessionUuid?: string
  status?: string
  trackingUuid?: string
  updatedAt?: string
  uuid?: string
  vehicleInformation?: LeadVehicleInformation
}

export interface FetchTrustedPartnersSuccess {
  partners: TrustedPartner[]
  type: ActionTypeKeys.FETCH_TRUSTED_PARTNERS_SUCCESS
}

export interface LeadEventMetadata {
  credit_score?: string
  isMobile?: boolean
  loan_amount?: string
  os?: string
  plaid_populated_fields_count?: number
  plaid_populated_then_changed_fields_count?: number
  purpose?: string
  recScore?: number
  uuid?: string
  selectedOfferSubType?: string
}

export interface SpendingCategory {
  displayName?: string
  name: string
  parentUuid?: string
  uuid: string
}

// Action type keys
export enum ActionTypeKeys {
  APPLICATION_STEP_LOADED = 'APPLICATION_STEP_LOADED',
  APPLICATION_STEP_SELECTED = 'APPLICATION_STEP_SELECTED',
  CALLED_GEOCODER = 'CALLED_GEOCODER',
  CLEAR_FILTERS = 'CLEAR_FILTERS',
  CREATE_ACCOUNT_SUCCESS = 'CREATE_ACCOUNT_SUCCESS',
  CREDIT_CARD_OFFER_CLICK = 'CREDIT_CARD_OFFER_CLICK',
  NON_APPLICATION_FILTER_CHANGED = 'NON_APPLICATION_FILTER_CHANGED',
  CREDIT_OFFER_PREVIEW_SELECTED = 'CREDIT_OFFER_PREVIEW_SELECTED',
  CREDIT_OFFER_TAB_SELECTED = 'CREDIT_OFFER_TAB_SELECTED',
  CREDIT_PREVIEW_OFFER_CLICKED = 'CREDIT_PREVIEW_OFFER_CLICKED',
  CREDIT_RATE_TABLE_PERSONALIZED_LOADED = 'CREDIT_RATE_TABLE_PERSONALIZED_LOADED',
  CREDIT_RATE_TABLE_PRE_APPROVED_OFFERS = 'CREDIT_RATE_TABLE_PRE_APPROVED_OFFERS',
  CREDIT_RATE_TABLE_PRE_QUALIFIED_OFFERS = 'CREDIT_RATE_TABLE_PRE_QUALIFIED_OFFERS',
  CREDIT_RATE_TABLE_PRE_SELECTED_OFFERS = 'CREDIT_RATE_TABLE_PRE_SELECTED_OFFERS',
  CREDIT_RATE_TABLE_PREVIEW_LOADED = 'CREDIT_RATE_TABLE_PREVIEW_LOADED',
  CREDIT_RESULTS_OFFER_CLICKED = 'CREDIT_RESULTS_OFFER_CLICKED',
  CST_EVENT = 'CST_EVENT',
  DEPOSITS_OFFER_CLICK = 'DEPOSITS_OFFER_CLICK',
  ENABLE_HYBRID_STEP = 'ENABLE_HYBRID_STEP',
  FETCH_OFFER_ERROR = 'FETCH_OFFER_ERROR',
  FETCH_OFFER_SUCCESS = 'FETCH_OFFER_SUCCESS',
  FETCH_PREVIEW_OFFERS_FAIL = 'FETCH_PREVIEW_OFFERS_FAIL',
  FETCH_PREVIEW_OFFERS_START = 'FETCH_PREVIEW_OFFERS_START',
  FETCH_PREVIEW_OFFERS_SUCCESS = 'FETCH_PREVIEW_OFFERS_SUCCESS',
  FETCH_RATE_TABLES_CANCEL = 'FETCH_RATE_TABLES_CANCEL',
  FETCH_RATE_TABLES_FAIL = 'FETCH_RATE_TABLES_FAIL',
  FETCH_RATE_TABLES_START = 'FETCH_RATE_TABLES_START',
  FETCH_RATE_TABLES_SUCCESS = 'FETCH_RATE_TABLES_SUCCESS',
  FETCH_TRUSTED_PARTNERS_SUCCESS = 'FETCH_TRUSTED_PARTNERS_SUCCESS',
  FETCH_ROUTER_QUESTIONS_ERROR = 'FETCH_ROUTER_QUESTIONS_ERROR',
  FETCH_ROUTER_QUESTIONS_START = 'FETCH_ROUTER_QUESTIONS_START',
  FETCH_ROUTER_QUESTIONS_SUCCESS = 'FETCH_ROUTER_QUESTIONS_SUCCESS',
  FETCH_REDEMPTION_TYPES_ERROR = 'FETCH_REDEMPTION_TYPES_ERROR',
  FETCH_REDEMPTION_TYPES_START = 'FETCH_REDEMPTION_TYPES_START',
  FETCH_REDEMPTION_TYPES_SUCCESS = 'FETCH_REDEMPTION_TYPES_SUCCESS',
  FILTERS_CHANGED = 'FILTERS_CHANGED',
  FLOW_SELECTED = 'FLOW_SELECTED',
  IMPRESSION = 'IMPRESSION',
  LEAD_INITIALIZED = 'LEAD_INITIALIZED',
  LIFE_INSURANCE_OFFER_SELECTED = 'LIFE_INSURANCE_OFFER_SELECTED',
  LIFE_INSURANCE_OFFER_DESELECTED = 'LIFE_INSURANCE_OFFER_DESELECTED',
  LIFE_INSURANCE_OFFER_CLICK = 'LIFE_INSURANCE_OFFER_CLICK',
  LIFE_INSURANCE_PHONE_CALL_REQUESTED = 'LIFE_INSURANCE_PHONE_CALL_REQUESTED',
  LIFE_INSURANCE_REPORT_PRODUCT_SUB_TYPE = 'LIFE_INSURANCE_REPORT_PRODUCT_SUB_TYPE',
  LOAD_CONFIG_BEGIN = 'LOAD_CONFIG_BEGIN',
  LOAD_CONFIG_SUCCESS = 'LOAD_CONFIG_SUCCESS',
  LOAD_PARAM = 'LOAD_PARAM',
  LOAD_PARENT_DATA = 'LOAD_PARENT_DATA',
  LOADED = 'LOADED',
  LOAN_OFFER_PREVIEW_SELECTED = 'LOAN_OFFER_PREVIEW_SELECTED',
  OFFER_AUTO_REDIRECT = 'OFFER_AUTO_REDIRECT',
  OFFER_CLICK = 'OFFER_CLICK',
  OFFER_MANUAL_REDIRECT = 'OFFER_MANUAL_REDIRECT',
  PLAID_ERROR = 'PLAID_ERROR',
  PLAID_INIT = 'PLAID_INIT',
  PLAID_LOADING = 'PLAID_LOADING',
  PLAID_SUCCESS = 'PLAID_SUCCESS',
  PLAID_MODAL_OPEN = 'PLAID_MODAL_OPEN',
  PUBLISH_LEAD_PREQUALIFIED_OR_PREAPPROVED = 'PUBLISH_LEAD_PREQUALIFIED_OR_PREAPPROVED',
  RATE_TABLE_CHANGED = 'RATE_TABLE_CHANGED',
  RATES_FILTER_REPLACED = 'RATES_FILTER_REPLACED',
  RATES_FILTER_SET = 'RATES_FILTER_SET',
  RATES_FILTER_TOGGLED = 'RATES_FILTER_TOGGLED',
  RATES_FILTERS_CLEARED = 'RATES_FILTERS_CLEARED',
  REQUEST_REDIRECT = 'REQUEST_REDIRECT',
  REWARD_VALUE_CALCULATION_ERROR = 'REWARD_VALUE_CALCULATION_ERROR',
  REWARD_VALUE_CALCULATION_START = 'REWARD_VALUE_CALCULATION_START',
  REWARD_VALUE_CALCULATION_SUCCESS = 'REWARD_VALUE_CALCULATION_SUCCESS',
  SEE_MORE_FROM_LENDER = 'SEE_MORE_FROM_LENDER',
  SESSION_INITIALIZED = 'SESSION_INITIALIZED',
  SET_MAX_RESULTS = 'SET_MAX_RESULTS',
  SET_RATE_TABLE_UUID = 'SET_RATE_TABLE_UUID',
  SORT_CHANGED = 'SORT_CHANGED',
  SORT_CLEARED = 'SORT_CLEARED',
  SPECIAL_OFFER_CLICK = 'SPECIAL_OFFER_CLICK',
  STEP_SELECTED = 'STEP_SELECTED',
  UPDATE_CONFIG_STEP = 'UPDATE_CONFIG_STEP',
  UPDATE_LEAD = 'UPDATE_LEAD',
  VIEWPORT_UPDATE = 'VIEWPORT_UPDATE',
  WAYPOINT_SEEN = 'WAYPOINT_SEEN',
}

// Action types
export interface CalledGeocoder {
  type: ActionTypeKeys.CALLED_GEOCODER
}

export interface CreditCardOfferPreviewSelected {
  offer: CreditOffer
  type: ActionTypeKeys.CREDIT_OFFER_PREVIEW_SELECTED
}

export interface ClearFilters {
  product: Product
  type: ActionTypeKeys.CLEAR_FILTERS
}

export interface CstEvent {
  type: ActionTypeKeys.CST_EVENT
  trackingData?: object
  trackingKey: TrackingKey
}

export interface CreditFilterCSTEventData {
  activeCardTypeFilters: string[]
  activeCardFeatureFilters: string[]
  rateTableUuid: string
}

export interface FetchOfferError {
  product: Product
  type: ActionTypeKeys.FETCH_OFFER_ERROR
}

export interface FetchRouterQuestionsStart {
  type: ActionTypeKeys.FETCH_ROUTER_QUESTIONS_START
  product: Product
}

export interface FetchRouterQuestionsError {
  errorStatus?: number
  product: Product
  type: ActionTypeKeys.FETCH_ROUTER_QUESTIONS_ERROR
}

export interface FetchRouterQuestionsSuccess {
  data: RouterQuestionsResponse
  product: Product
  type: ActionTypeKeys.FETCH_ROUTER_QUESTIONS_SUCCESS
}

export interface FetchRedemptionTypesStart {
  product: Product
  type: ActionTypeKeys.FETCH_REDEMPTION_TYPES_START
}

export interface FetchRedemptionTypesError {
  errorStatus?: number
  product: Product
  type: ActionTypeKeys.FETCH_REDEMPTION_TYPES_ERROR
}

export interface FetchRedemptionTypesSuccess {
  product: Product
  data: ApiResponse<RedemptionTypesResponse[]>
  type: ActionTypeKeys.FETCH_REDEMPTION_TYPES_SUCCESS
}

export interface FetchOfferSuccess {
  data: OfferDetail
  product: Product
  type: ActionTypeKeys.FETCH_OFFER_SUCCESS
}

export interface FetchPreviewOffersFailure {
  errorStatus?: number
  product: Product
  type: ActionTypeKeys.FETCH_PREVIEW_OFFERS_FAIL
}

export interface FetchPreviewOffersStart {
  product: Product
  type: ActionTypeKeys.FETCH_PREVIEW_OFFERS_START
  trackingKey: TrackingKey.PreviewOffersRequested
}

export interface FetchPreviewOffersSuccess {
  data
  product: Product
  publishKey: PublishKey.PreviewOffersDisplayed
  trackingData: {
    uuid: string
  }
  trackingKey: TrackingKey.PreviewOffersLoaded
  type: ActionTypeKeys.FETCH_PREVIEW_OFFERS_SUCCESS
}

export interface FetchRateTablesStart {
  product: Product
  type: ActionTypeKeys.FETCH_RATE_TABLES_START
  trackingKey: TrackingKey.RateTableRequested
}

export interface FetchRateTablesCancel {
  product: Product
  type: ActionTypeKeys.FETCH_RATE_TABLES_CANCEL
}

export interface FetchRateTablesSuccess {
  data
  product: Product
  publishKey: PublishKey.RateTableDisplayed
  trackingData: {
    uuid: string
  }
  trackingKey: TrackingKey.RateTableLoaded
  type: ActionTypeKeys.FETCH_RATE_TABLES_SUCCESS
}

export interface FetchRateTablesFailure {
  errorStatus?: number
  product: Product
  type: ActionTypeKeys.FETCH_RATE_TABLES_FAIL
}

interface BaseFiltersChanged {
  filters: Filters
  options?: FieldChangedOptions
  product: Product
  trackingKey: TrackingKey.FilterChange
  trackingData: Filters
  validationData: ValidationData
}

export interface FiltersChanged extends BaseFiltersChanged {
  type: ActionTypeKeys.FILTERS_CHANGED
}

export interface CreditFilterChanged extends BaseFiltersChanged {
  type: ActionTypeKeys.NON_APPLICATION_FILTER_CHANGED
}

export interface FlowSelected {
  flow: ProductFlow
  product: Product
  trackingData: {
    product: Product
    flow: ProductFlow
  }
  trackingKey: TrackingKey.FlowSelected
  type: ActionTypeKeys.FLOW_SELECTED
}

export interface LifeInsuranceOfferSelected {
  offer: LifeInsuranceOffer
  type: ActionTypeKeys.LIFE_INSURANCE_OFFER_SELECTED
}

export interface LifeInsuranceOfferDeselected {
  type: ActionTypeKeys.LIFE_INSURANCE_OFFER_DESELECTED
}

export interface Loaded {
  type: ActionTypeKeys.LOADED
  trackingKey: TrackingKey.Loaded
}

export interface LoadConfigBegin {
  type: ActionTypeKeys.LOAD_CONFIG_BEGIN
}

export interface LoadConfigSuccess {
  config: Config
  trackingData: object
  trackingKey: TrackingKey.PageLoad
  type: ActionTypeKeys.LOAD_CONFIG_SUCCESS
}

export interface LoadParam {
  param: QueryParamKeys
  type: ActionTypeKeys.LOAD_PARAM
  value: unknown
}

export interface LoadParentData {
  data: ParentData
  trackingData: ParentData
  trackingKey: TrackingKey.LoadParentData
  type: ActionTypeKeys.LOAD_PARENT_DATA
}

export interface LoanOfferPreviewSelected {
  offer: LoanOffer
  type: ActionTypeKeys.LOAN_OFFER_PREVIEW_SELECTED
}

export interface OfferAutoRedirect {
  offer: unknown
  type: ActionTypeKeys.OFFER_AUTO_REDIRECT
}

export interface OfferClick {
  lead: LeadEventMetadata
  publishKey: PublishKey.LoanOfferClick
  type: ActionTypeKeys.OFFER_CLICK
}

export interface OfferManualRedirect {
  offer: unknown
  type: ActionTypeKeys.OFFER_MANUAL_REDIRECT
}

export interface PlaidError {
  error: Error
  type: ActionTypeKeys.PLAID_ERROR
}

export interface PlaidLoading {
  loading: boolean
  type: ActionTypeKeys.PLAID_LOADING
}

export interface PlaidModalOpen {
  isModalOpen: boolean
  type: ActionTypeKeys.PLAID_MODAL_OPEN
}

export interface PlaidSuccess {
  type: ActionTypeKeys.PLAID_SUCCESS
}

export interface RateTableChanged {
  data
  product: Product
  type: ActionTypeKeys.RATE_TABLE_CHANGED
}

export interface RatesFilterReplaced {
  filterType: RatesFilter
  filterValue: unknown
  product: Product
  type: ActionTypeKeys.RATES_FILTER_REPLACED
}

export interface RatesFilterSet {
  filterType: RatesFilter
  filterValue: unknown
  product: Product
  type: ActionTypeKeys.RATES_FILTER_SET
}

export interface RatesFilterToggled {
  filterType: RatesFilter
  filterValue: unknown
  product: Product
  type: ActionTypeKeys.RATES_FILTER_TOGGLED
}

export interface RatesFiltersCleared {
  filterType?: RatesFilter
  filterValue?: unknown | undefined
  product: Product
  type: ActionTypeKeys.RATES_FILTERS_CLEARED
}

export interface SetMaxResults {
  max: number
  product: Product
  type: ActionTypeKeys.SET_MAX_RESULTS
}

export interface SetRateTableUuid {
  product: Product
  rateTableUuid: string
  type: ActionTypeKeys.SET_RATE_TABLE_UUID
}

export interface SortChanged {
  direction: SortDirection
  product: Product
  sort: SortOption
  type: ActionTypeKeys.SORT_CHANGED
  trackingData: {
    direction: SortDirection
    sort: SortOption
  }
  trackingKey: TrackingKey.SortChange
}

export interface SortCleared {
  product: Product
  type: ActionTypeKeys.SORT_CLEARED
}

export interface SpecialOfferClick {
  lead: LeadEventMetadata
  publishKey: PublishKey.SpecialOfferClick
  type: ActionTypeKeys.SPECIAL_OFFER_CLICK
}

export interface StepSelected {
  type: ActionTypeKeys.STEP_SELECTED
  product: Product
  step: ProductStep
  trackingKey: TrackingKey.StepSelected
  trackingData: {
    product: Product
    step: ProductStep
  }
  isSteppingBack: boolean
}

export interface EnableCCHybridOfferList {
  type: ActionTypeKeys.ENABLE_HYBRID_STEP
}

export interface ApplicationStepSelected {
  type: ActionTypeKeys.APPLICATION_STEP_SELECTED
  product: Product
  applicationStep: ApplicationStep
  trackingKey: ApplicationStepSelected
  trackingData: {
    product: Product
    applicationStep: ApplicationStep
  }
}

export interface UpdateLead {
  type: ActionTypeKeys.UPDATE_LEAD
}

export interface UpdateConfigStep {
  type: ActionTypeKeys.UPDATE_CONFIG_STEP
  step: ProductStep
}

export interface ApplicationStepLoaded {
  publishKey: PublishKey.ApplicationLoaded
  type: ActionTypeKeys.APPLICATION_STEP_LOADED
}

export interface LeadInitialized {
  lead: LeadEventMetadata
  publishKey: PublishKey.LeadInitialized
  type: ActionTypeKeys.LEAD_INITIALIZED
}

export interface SessionInitialized {
  sessionUuid: string
  trackingData: {
    sessionUuid: string
  }
  trackingKey: 'session-initialized' // TrackingKey.SessionInitialized
  type: ActionTypeKeys.SESSION_INITIALIZED
}

export interface Impression {
  trackingUuid: string
  publishKey: PublishKey.Impression
  type: ActionTypeKeys.IMPRESSION
}

export interface DepositsOfferClick {
  lead: LeadEventMetadata
  publishKey: PublishKey.DepositsOfferClick
  type: ActionTypeKeys.DEPOSITS_OFFER_CLICK
}

export interface CreditCardOfferClick {
  lead: LeadEventMetadata
  publishKey: PublishKey.CreditCardOfferClick
  type: ActionTypeKeys.CREDIT_CARD_OFFER_CLICK
}

export interface LifeInsuranceOfferClick {
  lead: LeadEventMetadata
  publishKey: PublishKey.LifeInsuranceOfferClick
  type: ActionTypeKeys.LIFE_INSURANCE_OFFER_CLICK
}

export interface LifeInsurancePhoneCallRequested {
  lead: LeadEventMetadata
  publishKey: PublishKey.LifeInsurancePhoneCallRequested
  type: ActionTypeKeys.LIFE_INSURANCE_PHONE_CALL_REQUESTED
}

export interface LifeInsuranceReportProductSubType {
  productSubType: LifeInsuranceProductSubType
  publishKey: PublishKey.LifeInsuranceReportProductSubType
  type: ActionTypeKeys.LIFE_INSURANCE_REPORT_PRODUCT_SUB_TYPE
}

export type ViewportUpdate = { type: ActionTypeKeys.VIEWPORT_UPDATE } & ViewportUpdateMessageBody

export interface WaypointSeen {
  name: Waypoints
  trackingKey: Waypoints
  type: ActionTypeKeys.WAYPOINT_SEEN
}

export interface RewardValueCalculationStart {
  rateTableUuid: string
  product: Product
  type: ActionTypeKeys.REWARD_VALUE_CALCULATION_START
}

export interface RewardValueCalculationFailure {
  errorStatus?: number
  rateTableUuid: string
  product: Product
  type: ActionTypeKeys.REWARD_VALUE_CALCULATION_ERROR
}

export interface RewardValueCalculationSuccess {
  data: RewardValueCalculationResponse
  product: Product
  type: ActionTypeKeys.REWARD_VALUE_CALCULATION_SUCCESS
}

export interface CreateAccountSuccess {
  type: ActionTypeKeys.CREATE_ACCOUNT_SUCCESS
}

interface PublishLeadPreQualifiedOrPreApproved {
  leadUuid: string
  publishKey: PublishKey.LeadPreQualifiedOrPreApproved
  type: ActionTypeKeys.PUBLISH_LEAD_PREQUALIFIED_OR_PREAPPROVED
}

export type ActionTypes =
  | ApplicationStepLoaded
  | ApplicationStepSelected
  | CalledGeocoder
  | ClearFilters
  | CreateAccountSuccess
  | CreditCardOfferClick
  | CreditCardOfferPreviewSelected
  | CreditFilterChanged
  | DepositsOfferClick
  | EnableCCHybridOfferList
  | FetchOfferError
  | FetchOfferSuccess
  | FetchPreviewOffersFailure
  | FetchPreviewOffersStart
  | FetchPreviewOffersSuccess
  | FetchRateTablesCancel
  | FetchRateTablesFailure
  | FetchRateTablesStart
  | FetchRateTablesSuccess
  | FetchRouterQuestionsStart
  | FetchRouterQuestionsError
  | FetchRouterQuestionsSuccess
  | FetchRedemptionTypesStart
  | FetchRedemptionTypesError
  | FetchRedemptionTypesSuccess
  | FetchTrustedPartnersSuccess
  | FiltersChanged
  | FlowSelected
  | Impression
  | LeadInitialized
  | LifeInsuranceOfferSelected
  | LifeInsuranceOfferDeselected
  | LifeInsuranceOfferClick
  | LifeInsurancePhoneCallRequested
  | LifeInsuranceReportProductSubType
  | LoadConfigBegin
  | LoadConfigSuccess
  | LoadParam
  | LoadParentData
  | LoanOfferPreviewSelected
  | OfferAutoRedirect
  | OfferClick
  | OfferManualRedirect
  | PlaidError
  | PlaidLoading
  | PlaidModalOpen
  | PlaidSuccess
  | PublishLeadPreQualifiedOrPreApproved
  | RateTableChanged
  | RatesFilterReplaced
  | RatesFilterSet
  | RatesFilterToggled
  | RatesFiltersCleared
  | RewardValueCalculationFailure
  | RewardValueCalculationStart
  | RewardValueCalculationSuccess
  | SetMaxResults
  | SetRateTableUuid
  | SortChanged
  | SortCleared
  | SpecialOfferClick
  | StepSelected
  | UpdateConfigStep
  | UpdateLead
  | ViewportUpdate
  | WaypointSeen

// Actions
export const sortChangedAction = (direction: SortDirection, product: Product, sort: SortOption): SortChanged => ({
  direction,
  product,
  sort,
  trackingData: {
    direction,
    sort,
  },
  trackingKey: TrackingKey.SortChange,
  type: ActionTypeKeys.SORT_CHANGED,
})

// Action creators
export const createAccountSuccess = (): CreateAccountSuccess => ({ type: ActionTypeKeys.CREATE_ACCOUNT_SUCCESS })

export const waypointSeen = (name: Waypoints): WaypointSeen => ({
  name,
  trackingKey: name,
  type: ActionTypeKeys.WAYPOINT_SEEN,
})

export const viewportUpdate = (data: ViewportUpdateMessageBody): ViewportUpdate => ({
  ...data,
  type: ActionTypeKeys.VIEWPORT_UPDATE,
})

export const clearFilters = (product: Product): ClearFilters => ({
  product,
  type: ActionTypeKeys.CLEAR_FILTERS,
})

export const fetchOfferError = (product: Product): FetchOfferError => ({
  product,
  type: ActionTypeKeys.FETCH_OFFER_ERROR,
})

export const fetchOfferSuccess = (product: Product, data): FetchOfferSuccess => ({
  data,
  product,
  type: ActionTypeKeys.FETCH_OFFER_SUCCESS,
})

export const fetchPreviewOffersFailure = (product: Product, status?): FetchPreviewOffersFailure => ({
  errorStatus: status,
  product,
  type: ActionTypeKeys.FETCH_PREVIEW_OFFERS_FAIL,
})

export const fetchPreviewOffersStart = (product: Product): FetchPreviewOffersStart => ({
  product,
  trackingKey: TrackingKey.PreviewOffersRequested,
  type: ActionTypeKeys.FETCH_PREVIEW_OFFERS_START,
})

export const fetchPreviewOffersSuccess = (product: Product, data): FetchPreviewOffersSuccess => ({
  data,
  product,
  publishKey: PublishKey.PreviewOffersDisplayed,
  trackingData: { uuid: data.uuid },
  trackingKey: TrackingKey.PreviewOffersLoaded,
  type: ActionTypeKeys.FETCH_PREVIEW_OFFERS_SUCCESS,
})

export const fetchRouterQuestionsStart = (): FetchRouterQuestionsStart => ({
  type: ActionTypeKeys.FETCH_ROUTER_QUESTIONS_START,
  product: Product.LifeInsurance,
})

export const fetchRouterQuestionsSuccess = (response: RouterQuestionsResponse): FetchRouterQuestionsSuccess => ({
  data: response,
  type: ActionTypeKeys.FETCH_ROUTER_QUESTIONS_SUCCESS,
  product: Product.LifeInsurance,
})

export const fetchRouterQuestionsError = (
  product: Product = Product.LifeInsurance,
  errorStatus?: number,
): FetchRouterQuestionsError => ({
  errorStatus,
  product,
  type: ActionTypeKeys.FETCH_ROUTER_QUESTIONS_ERROR,
})

export const fetchRedemptionTypesStart = (product: Product = Product.CreditCard): FetchRedemptionTypesStart => ({
  product,
  type: ActionTypeKeys.FETCH_REDEMPTION_TYPES_START,
})

export const fetchRedemptionTypesSuccess = (
  product: Product = Product.CreditCard,
  response: ApiResponse<RedemptionTypesResponse[]>,
): FetchRedemptionTypesSuccess => ({
  product,
  data: response,
  type: ActionTypeKeys.FETCH_REDEMPTION_TYPES_SUCCESS,
})

export const fetchRedemptionTypesError = (
  product: Product = Product.CreditCard,
  errorStatus?,
): FetchRedemptionTypesError => ({
  errorStatus,
  product,
  type: ActionTypeKeys.FETCH_REDEMPTION_TYPES_ERROR,
})

export const fetchRateTablesStart = (product: Product): FetchRateTablesStart => ({
  product,
  trackingKey: TrackingKey.RateTableRequested,
  type: ActionTypeKeys.FETCH_RATE_TABLES_START,
})

export const fetchRateTablesCancel = (product: Product): FetchRateTablesCancel => ({
  product,
  type: ActionTypeKeys.FETCH_RATE_TABLES_CANCEL,
})

export const fetchRateTablesSuccess = (product: Product, data): FetchRateTablesSuccess => ({
  data,
  product,
  publishKey: PublishKey.RateTableDisplayed,
  trackingData: { uuid: data.uuid },
  trackingKey: TrackingKey.RateTableLoaded,
  type: ActionTypeKeys.FETCH_RATE_TABLES_SUCCESS,
})

export const fetchRateTablesFailure = (product: Product, errorStatus?): FetchRateTablesFailure => ({
  errorStatus,
  product,
  type: ActionTypeKeys.FETCH_RATE_TABLES_FAIL,
})

export const calledGeocoder = (): CalledGeocoder => ({
  type: ActionTypeKeys.CALLED_GEOCODER,
})

const offerClick = (metadata: LeadEventMetadata): OfferClick => ({
  lead: metadata,
  publishKey: PublishKey.LoanOfferClick,
  type: ActionTypeKeys.OFFER_CLICK,
})

export const plaidError = (error: Error, startTime: Date) => ({
  error,
  trackingData: {
    fieldCount: 0,
    status: ActionTypeKeys.PLAID_ERROR,
    timeElapsed: new Date().getTime() - startTime.getTime(),
  },
  trackingKey: TrackingKey.PlaidLinkResult,
  type: ActionTypeKeys.PLAID_ERROR,
})

export const plaidLoading = (loading: boolean) => ({
  loading,
  type: ActionTypeKeys.PLAID_LOADING,
})

export const plaidModalOpen = (isModalOpen: boolean) => ({
  isModalOpen,
  type: ActionTypeKeys.PLAID_MODAL_OPEN,
})

export const plaidSuccess = (startTime: Date, fieldsCaptured: string[] = []) => ({
  trackingData: {
    fieldCount: fieldsCaptured.length,
    fields: fieldsCaptured,
    status: ActionTypeKeys.PLAID_SUCCESS,
    timeElapsed: new Date().getTime() - startTime.getTime(),
  },
  trackingKey: TrackingKey.PlaidLinkResult,
  type: ActionTypeKeys.PLAID_SUCCESS,
})

export interface PlaidExchangeTokenResponse {
  item_id: string
}

export const plaidInit = (product: Product) => ({
  trackingData: {
    product,
  },
  trackingKey: TrackingKey.PlaidLinkInit,
  type: ActionTypeKeys.PLAID_INIT,
})

export const connectPlaid = (product: Product, publicKey: string) => (dispatch) => {
  const connectPlaidUrl = `${consumerApiUrl}/api/plaid/exchangeToken`

  const leadUuid = Session.getLeadUuid()
  const data = {
    lead_uuid: leadUuid,
    public_token: publicKey,
  }

  // Show the loading indicator
  dispatch(plaidLoading(true))

  Api.post(connectPlaidUrl, data)
    .then((response: ApiResponse<PlaidExchangeTokenResponse>) => {
      dispatch(refreshPlaidData(product, response.data.item_id, leadUuid, new Date()))
    })
    .catch((error) => {
      dispatch(plaidError(error, new Date()))
      Sentry.captureException(error)
    })
}

const refreshPlaidData = (product: Product, itemId: string, leadUuid: string, startTime: Date) => (dispatch) => {
  const refreshPlaidDataUrl = `${consumerApiUrl}/api/plaid/userData`
  const data = {
    asset_report_id: itemId,
    lead_uuid: leadUuid,
  }
  const maxWaitMilliseconds = 10000 // Max wait of 60s
  const pollRate = 500 // Poll every 1s

  Api.post(refreshPlaidDataUrl, data)
    .then((response: ApiResponse) => {
      const hasPassedMaxWaitTime = startTime.getTime() + maxWaitMilliseconds < new Date().getTime()

      if (response.status === 200) {
        // Populate the application fields with the data
        Object.keys(response.data).forEach((field) => {
          dispatch(
            applicationFieldChanged(product, field, response.data[field], true, true, {
              dataSource: FieldDataSources.Plaid,
            }),
          )
        })

        // If Plaid returns a zipcode, but not a city and state, get it from the geocoder
        const missingCityOrState =
          Object.keys(response.data).includes(Filter.Zipcode) &&
          (!Object.keys(response.data).includes(Filter.City) || !Object.keys(response.data).includes(Filter.State))
        if (missingCityOrState) {
          dispatch(fetchCityStateFromZip(product, response.data[Filter.Zipcode]))
        }

        dispatch(plaidSuccess(startTime, Object.keys(response.data)))
      } else if (hasPassedMaxWaitTime) {
        const error = new Error('Fetching plaid assets timed out timed out')
        dispatch(plaidError(error, startTime))
        Sentry.captureException(error)
      } else {
        // Continue to poll
        setTimeout(() => dispatch(refreshPlaidData(product, itemId, leadUuid, startTime)), pollRate)
      }
    })
    .catch((error) => {
      dispatch(plaidError(error, startTime))
      Sentry.captureException(error)
    })
}

const specialOfferClick = (metadata: LeadEventMetadata): SpecialOfferClick => ({
  lead: metadata,
  publishKey: PublishKey.SpecialOfferClick,
  type: ActionTypeKeys.SPECIAL_OFFER_CLICK,
})

const rateTableChanged = (product: Product, data): RateTableChanged => ({
  data,
  product,
  type: ActionTypeKeys.RATE_TABLE_CHANGED,
})

export const applicationStepLoaded = (): ApplicationStepLoaded => ({
  publishKey: PublishKey.ApplicationLoaded,
  type: ActionTypeKeys.APPLICATION_STEP_LOADED,
})

export const leadInitialized = (metadata: LeadEventMetadata): LeadInitialized => ({
  lead: metadata,
  publishKey: PublishKey.LeadInitialized,
  type: ActionTypeKeys.LEAD_INITIALIZED,
})

export const lifeInsuranceOfferSelected = (offer: LifeInsuranceOffer) => (dispatch) => {
  dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.PersonalInfo))

  dispatch({
    offer,
    type: ActionTypeKeys.LIFE_INSURANCE_OFFER_SELECTED,
    trackingData: { offer: { uuid: offer.uuid, packageType: offer.details.packageType } },
    trackingKey: TrackingKey.LifeInsuranceOfferSelected,
  })

  Parent.updateUrl(OFFER_URL_KEY, offer.uuid)
}

export const lifeInsuranceOfferDeselected = () => (dispatch) => {
  dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.Results))

  dispatch({
    type: ActionTypeKeys.LIFE_INSURANCE_OFFER_DESELECTED,
  })

  Parent.removeFromUrl(OFFER_URL_KEY)
}

export const sessionInitialized = ({ sessionUuid }: { sessionUuid: string }): SessionInitialized => ({
  sessionUuid,
  trackingData: { sessionUuid },
  trackingKey: 'session-initialized', // TrackingKey.SessionInitialized,
  type: ActionTypeKeys.SESSION_INITIALIZED,
})

export const impression = (trackingUuid: string): Impression => ({
  publishKey: PublishKey.Impression,
  trackingUuid,
  type: ActionTypeKeys.IMPRESSION,
})

export const depositsOfferClick = (metadata: LeadEventMetadata): DepositsOfferClick => ({
  lead: metadata,
  publishKey: PublishKey.DepositsOfferClick,
  type: ActionTypeKeys.DEPOSITS_OFFER_CLICK,
})

export const creditCardOfferClick = (metadata: LeadEventMetadata): CreditCardOfferClick => ({
  lead: metadata,
  publishKey: PublishKey.CreditCardOfferClick,
  type: ActionTypeKeys.CREDIT_CARD_OFFER_CLICK,
})

export const lifeInsuranceOfferClick = (metadata: LeadEventMetadata): LifeInsuranceOfferClick => ({
  lead: metadata,
  publishKey: PublishKey.LifeInsuranceOfferClick,
  type: ActionTypeKeys.LIFE_INSURANCE_OFFER_CLICK,
})

const getTphPixelHackLifeInsuranceUrl = (selectedOffer: LifeInsuranceOffer, state: MultiProductState) => {
  const payout = selectedOffer.productSubType === LifeInsuranceProductSubType.TermLife ? '100' : '20'
  const transactionId = state.config.clientTags?.subid?.[0]
  return `http://t.thepennyhoarder.com/aff_lsr?offer_id=6456&transaction_id=${transactionId}&payout=${payout}`
}

export const lifeInsurancePhoneCallRequested = (offer?: LifeInsuranceOffer) => (
  dispatch: MultiProductDispatch,
  getState: () => MultiProductState,
) => {
  const state = getState()
  if (offer !== undefined && configCompanyUuidSelector(state) === TPH_LIFE_INSURANCE_COMPANY_UUID) {
    fetch(getTphPixelHackLifeInsuranceUrl(offer, state), { method: 'GET' })
  }
  return dispatch({
    lead: {
      isMobile: Bowser.mobile,
      os: Bowser.osname,
      recScore: offer?.recommendationScore,
      uuid: Session.getLeadUuid(),
    },
    publishKey: PublishKey.LifeInsurancePhoneCallRequested,
    type: ActionTypeKeys.LIFE_INSURANCE_PHONE_CALL_REQUESTED,
  })
}

export const lifeInsuranceReportProductSubType = (
  productSubType: LifeInsuranceProductSubType,
): LifeInsuranceReportProductSubType => ({
  productSubType,
  publishKey: PublishKey.LifeInsuranceReportProductSubType,
  type: ActionTypeKeys.LIFE_INSURANCE_REPORT_PRODUCT_SUB_TYPE,
})

export const ratesFilterReplaced = (
  product: Product,
  filterType: RatesFilter,
  filterValue: unknown,
): RatesFilterReplaced => ({
  filterType,
  filterValue,
  product,
  type: ActionTypeKeys.RATES_FILTER_REPLACED,
})

export const ratesFilterToggled = (
  product: Product,
  filterType: RatesFilter,
  filterValue: unknown,
): RatesFilterToggled => ({
  filterType,
  filterValue,
  product,
  type: ActionTypeKeys.RATES_FILTER_TOGGLED,
})

export const ratesFilterSet = (product: Product, filterType: RatesFilter, filterValue: unknown): RatesFilterSet => ({
  filterType,
  filterValue,
  product,
  type: ActionTypeKeys.RATES_FILTER_SET,
})

export const trackCreditFilterChanges = (trackingKey: TrackingKey, data?: CreditFilterCSTEventData) => ({
  trackingData: { data },
  trackingKey,
  type: ActionTypeKeys.CST_EVENT,
})

export const trackRatesFilterChange = (product: Product, filterType: RatesFilter, filterValue: unknown): CstEvent => ({
  trackingData: {
    filterType,
    filterValue,
    product,
  },
  trackingKey: TrackingKey.RatesFilterChanged,
  type: ActionTypeKeys.CST_EVENT,
})

export const ratesFilterChanged = (
  product: Product,
  filterType: RatesFilter,
  filterValue: unknown,
  replace: boolean,
  userIntent = false,
) => (dispatch) => {
  const ratesFilterChangeAction = replace ? ratesFilterReplaced : ratesFilterToggled
  dispatch(ratesFilterChangeAction(product, filterType, filterValue))
  if (userIntent) {
    dispatch(trackRatesFilterChange(product, filterType, filterValue))
  }
}

export const ratesFiltersCleared = (
  product: Product,
  filterType?: RatesFilter,
  filterValue?: unknown | undefined,
): RatesFiltersCleared => ({
  filterType,
  filterValue,
  product,
  type: ActionTypeKeys.RATES_FILTERS_CLEARED,
})

export const rewardValueCalculationStart = (rateTableUuid: string): RewardValueCalculationStart => ({
  product: Product.CreditCard,
  rateTableUuid,
  type: ActionTypeKeys.REWARD_VALUE_CALCULATION_START,
})

export const rewardValueCalculationSuccess = (data: RewardValueCalculationResponse): RewardValueCalculationSuccess => ({
  data,
  product: Product.CreditCard,
  type: ActionTypeKeys.REWARD_VALUE_CALCULATION_SUCCESS,
})

export const rewardValueCalculationFailure = (rateTableUuid: string): RewardValueCalculationFailure => ({
  product: Product.CreditCard,
  rateTableUuid,
  type: ActionTypeKeys.REWARD_VALUE_CALCULATION_ERROR,
})

export const updateConfigStep = (step: ProductStep): UpdateConfigStep => ({
  step,
  type: ActionTypeKeys.UPDATE_CONFIG_STEP,
})

export const enableCCHybridOfferList = () => ({
  type: ActionTypeKeys.ENABLE_HYBRID_STEP,
})

export interface GoogleAnalyticsEventData {
  action: string
  dimensions?: GoogleAnalyticsMetadataFields
  label?: string
  product?: Product
  step?: ProductStep
  value?: number
}

export interface GoogleAnalyticsPageviewData {
  dimensions?: GoogleAnalyticsMetadataFields
  title?: string
  product?: Product
  step?: ProductStep
}

export const trackGoogleAnalytics = (
  eventType: GoogleAnalyticsEventTypes,
  data: GoogleAnalyticsEventData | GoogleAnalyticsPageviewData,
) => (_, getState) => {
  const state = getState()

  const companyUuid = state.config[ConfigKeys.CompanyUuid]
  const partnerKey = state.config[ConfigServiceDataKeys.partner]
  const selectedProduct = data.product || state?.ui?.selectedProduct
  const selectedStep = data.step || get(state, `ui.selectedStep.${selectedProduct}`)
  const selectedFlow = state.config[FlowQueryParamForProduct[selectedProduct]]

  const customDimensions: GoogleAnalyticsMetadataFields = {
    [GoogleAnalyticsMetadataKeys.CompanyUuid]: companyUuid,
    [GoogleAnalyticsMetadataKeys.ConsumerBrand]: brandFromHost ?? '',
    [GoogleAnalyticsMetadataKeys.Flow]: selectedFlow,
    [GoogleAnalyticsMetadataKeys.PartnerKey]: partnerKey,
    [GoogleAnalyticsMetadataKeys.Step]: selectedStep,
    [GoogleAnalyticsMetadataKeys.TrackingUuid]: Session.trackingUuid ?? '',
    [GoogleAnalyticsMetadataKeys.Type]: selectedProduct,
    ...data.dimensions,
  }

  const defaultTitle = [partnerKey, selectedProduct, selectedStep].join(':')
  const path = `/${selectedProduct}/${selectedStep}`

  switch (eventType) {
    case GoogleAnalyticsEventTypes.Event:
      const eventData = data as GoogleAnalyticsEventData
      GoogleAnalytics.sendEvent({
        action: eventData.action,
        category: selectedProduct,
        count: eventData.value,
        fields: customDimensions,
        label: eventData.label || selectedProduct,
        path,
        title: defaultTitle,
      })
      break
    case GoogleAnalyticsEventTypes.Pageview:
      const pageviewData = data as GoogleAnalyticsPageviewData

      GoogleAnalytics.sendPageview({
        fields: customDimensions,
        path,
        title: pageviewData.title || defaultTitle,
      })
      break
    default:
  }
}

export const trackGoogleAnalyticsEvent = (
  action: string,
  value?: number,
  product?: Product,
  label?: string,
  dimensions?: GoogleAnalyticsMetadataFields,
) => (dispatch) => {
  dispatch(
    trackGoogleAnalytics(GoogleAnalyticsEventTypes.Event, {
      action,
      dimensions,
      label,
      product,
      value,
    }),
  )
}

export const trackGoogleAnalyticsPageview = (product?: Product, step?: ProductStep) => (dispatch) => {
  dispatch(trackGoogleAnalytics(GoogleAnalyticsEventTypes.Pageview, { product, step }))
}

const publishLeadPreQualifiedOrPreApproved = (leadUuid: string): PublishLeadPreQualifiedOrPreApproved => ({
  leadUuid,
  publishKey: PublishKey.LeadPreQualifiedOrPreApproved,
  type: ActionTypeKeys.PUBLISH_LEAD_PREQUALIFIED_OR_PREAPPROVED,
})

export const fetchRateTableFromState = (product: Product, maxWaitMilliseconds = 28000) => (dispatch, getState) => {
  dispatch(fetchRateTablesStart(product))
  // eslint-disable-next-line no-use-before-define
  dispatch(refreshRateTable(product, maxWaitMilliseconds)).catch((error) => {
    // If we fail to fetch a pre-existing rate table (because of e.g. an incorrect results param),
    // go back to the previous step
    let revertToStepAfterFailure
    switch (product) {
      case Product.Deposits:
        revertToStepAfterFailure = DepositsStep.OfferList
        break
      case Product.Loan:
        revertToStepAfterFailure = LoanStep.Application
        break
      default:
    }
    // eslint-disable-next-line no-use-before-define
    dispatch(stepSelected(product, revertToStepAfterFailure))
  })
}

export const setRateTableUuid = (product: Product, rateTableUuid: string) => ({
  product,
  rateTableUuid,
  type: ActionTypeKeys.SET_RATE_TABLE_UUID,
})

const refreshRateTable = (product: Product, maxWaitMilliseconds: number) => async (dispatch, getState) => {
  const rateTableUuid = get(getState(), `rates.${product}.uuid`)

  try {
    const response = await Api.get(`/originator/rateTables/${rateTableUuid}`)
    return dispatch(pollForRateTable(product, response, maxWaitMilliseconds))
  } catch (error) {
    captureRefreshRateTablesError(product, rateTableUuid, error)
    return Promise.reject(error)
  }
}

export const getTrustedParthers = () => Api.get('/uiUtil/featuredFinancialInstitutions?limit=10')

export const fetchSpendingCategories = async (): Promise<SpendingCategory[]> =>
  Api.get('/offerAugmentation/spendingCategories')
    .then(async ({ data }: { data: SpendingCategory[] }) => Promise.resolve(data))
    .catch(async (error) => {
      captureSpendingCategoriesRequestError(error)
      return Promise.reject(error)
    })

// A function to set the initial spending category amounts based on a list of categories and the
// applicant's annual expenditure. We'll use this to pre-populate the category fields, either
// with the list of spending categories the API returned, or with the default list if that fails.
export const setInitialSpendingCategoryAmounts = (categories: SpendingCategory[], annualIncome = 0): object => {
  const initialAverageMonthlySpend = Math.round((annualIncome * AVERAGE_EXPENDITURE) / 12)
  const initialSpendingCategoryAmounts = {}
  categories.forEach((category) => {
    initialSpendingCategoryAmounts[category.uuid] =
      initialAverageMonthlySpend * DEFAULT_SPENDING_CATEGORY_PERCENTAGES[category.uuid]
  })
  return initialSpendingCategoryAmounts
}

export const fetchRewardValueCalculation = (selectedRewardsPreferenceUuid?: string) => async (dispatch, getState) => {
  const state: CommonState = getState()
  const rateTableUuid = creditCardRatesUuidSelector(state)

  dispatch(rewardValueCalculationStart(rateTableUuid))
  const rewardsPreferenceUuid = selectedRewardsPreferenceUuid
    ? selectedRewardsPreferenceUuid
    : (typeof creditCardApplicationRewardsPreferenceUuidSelector(state) === 'string' &&
        creditCardApplicationRewardsPreferenceUuidSelector(state)) ||
      DEFAULT_REDEMPTION_TYPE_VALUES[RewardsPreferenceTypeOptions.FLIGHTS]

  const annualIncome = filtersCreditAnnualIncomeSelector(state)
  let spendingCategories: SpendingCategory[]
  try {
    // Check for spendingCategories from Api
    spendingCategories = await fetchSpendingCategories()
  } catch (e) {
    // if none, use default values
    spendingCategories = DEFAULT_SPENDING_CATEGORIES
  }

  const initialSpendingCategoryAmounts = setInitialSpendingCategoryAmounts(spendingCategories, annualIncome)
  const aggregates: RewardsValueAggregate[] = []

  if (filtersCreditSpendingCalculatorSelector(state)) {
    const spendingCalculatorAmounts = filtersCreditSpendingCalculatorSelector(state)

    for (const key in spendingCalculatorAmounts) {
      if (spendingCalculatorAmounts.hasOwnProperty(key)) {
        aggregates.push({
          amount: spendingCalculatorAmounts[key],
          uuid: key,
        })
      }
    }
  } else {
    for (const key in initialSpendingCategoryAmounts) {
      if (initialSpendingCategoryAmounts.hasOwnProperty(key)) {
        aggregates.push({
          amount: initialSpendingCategoryAmounts[key],
          uuid: key,
        })
      }
    }
  }

  const redemptionTypes = filtersCreditRedemptionTypesSelector(state)

  const weightedRedemptionTypes: RedemptionType[] | undefined =
    redemptionTypes !== undefined
      ? Object.keys(redemptionTypes).map((type) => {
          const uuid = redemptionTypes[type]
          return {
            redemptionTypeUuid: uuid,
            weight: uuid === rewardsPreferenceUuid ? 1 : 0,
          }
        })
      : undefined

  const requestBody: RewardsValueCalculationRequestBody = {
    aggregates,
    preferredRedemptionTypes: weightedRedemptionTypes,
    rateTableUuid,
  }

  let rewardValueCalculatorResponse: ApiResponse<RewardValueCalculationResponse>

  try {
    rewardValueCalculatorResponse = await Api.post('/offerAugmentation/rewardValueCalculation', requestBody)
    dispatch(rewardValueCalculationSuccess(rewardValueCalculatorResponse.data))

    // if there are offers with rewards data sort by them else sort by recommended
    const creditCardOffers = creditCardRatesOffersSelector(getState())
    const offersHaveTotalValue = creditCardOffers.some(
      (offer) => !!((offer.details.earningProgram?.personalization?.totalValue ?? 0) > 0),
    )

    if (offersHaveTotalValue) {
      dispatch(sortChanged(SortDirection.Desc, Product.CreditCard, CreditSortOptionUI.Rewards))
    } else {
      dispatch(sortChanged(SortDirection.Desc, Product.CreditCard, CreditSortOptionUI.Recommended))
    }
  } catch (error) {
    const currentSpendingCalculator: SpendingCalculatorFields = filtersCreditSpendingCalculatorSelector(state)
    const defaultCalculatorValues: SpendingCalculatorFields = {}

    for (const key in currentSpendingCalculator) {
      if (currentSpendingCalculator.hasOwnProperty(key)) {
        defaultCalculatorValues[key] = 0
      }
    }

    dispatch(rewardValueCalculationFailure(rateTableUuid))
    dispatch(filterChanged(Product.CreditCard, Filter.SpendingCalculator, defaultCalculatorValues))
    dispatch(sortChanged(SortDirection.Desc, Product.CreditCard, CreditSortOptionUI.Recommended))
  }
}

const processPreviewRateTableResponse = (product: Product, response) => (dispatch, getState) => {
  const offers = get(response, `data.${ProductResponseKeys[product]}Offers`, [])
  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.LoadedResults, offers.length))
  if (product === Product.CreditCard) {
    dispatch(processCreditCardOffers(offers))
  }
  dispatch(fetchPreviewOffersSuccess(product, response.data))
}

const processRateTableResponse = (product: Product, response) => (dispatch, getState) => {
  const state = getState()
  const offers = get(response, `data.${ProductResponseKeys[product]}Offers`, [])

  switch (product) {
    case Product.Loan:
      const preApprovedOrPreQualified = offers.some((offer: LoanOffer) => offer.preQualified || offer.preApproved)
      if (preApprovedOrPreQualified) {
        dispatch(publishLeadPreQualifiedOrPreApproved(loanRatesLeadUuidSelector(state)))
      }
      break
    case Product.CreditCard:
      dispatch(processCreditCardOffers(offers))
      break
    default:
  }

  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.LoadedResults, offers.length))
  dispatch(fetchRateTablesSuccess(product, response.data))
}

const pollForRateTable = (product: Product, response, maxWaitMilliseconds: number) => (dispatch, getState) => {
  const state = getState()
  const startTime = loadingStartTimeSelector(state, product)

  if (
    (startTime !== undefined && startTime.getTime() + maxWaitMilliseconds < new Date().getTime()) ||
    (!get(response, 'data.pendingResponses.length') && !get(response, 'data.pendingOriginators.length'))
  ) {
    dispatch(processRateTableResponse(product, response))
  } else {
    dispatch(rateTableChanged(product, response.data))

    // Poll every 100ms for 4 seconds, then poll at 1s
    const pollRate = startTime !== undefined && startTime.getTime() + 4000 > new Date().getTime() ? 100 : 1000
    setTimeout(() => dispatch(refreshRateTable(product, maxWaitMilliseconds)), pollRate)
  }

  if (
    get(response, 'data.pendingResponses.length', 0) === 0 &&
    get(response, 'data.creditCardOffers.length', 0) === 0 &&
    get(response, 'data.loanOffers.length', 0) === 0 &&
    get(response, 'data.savingsOffers.length', 0) === 0 &&
    get(response, 'data.specialOffers.length', 0) === 0 &&
    get(response, 'data.lifeInsuranceOffers.length', 0) === 0
  ) {
    captureZeroOffersWarning(get(response, 'data.uuid'), product)
  }
}

const tphPixelHackCreditCard = (offers: CreditOffer[]) => (dispatch, getState) => {
  const state = getState()
  const cookieKey = `_subid_${TPH_AMEX_COMPANY_UUID}`

  if (!Cookies.get(cookieKey)) {
    const transactionId = get(state, 'config.clientTags.subid.0')
    Cookies.set(cookieKey, transactionId, { expires: 31536000000, sameSite: 'none', secure: true })
    // if AMEX offer returned, payout = 5
    const payout = !!offers.find((offer) => offer.partner?.uuid === '78870403-edf9-4898-a887-3faf07e2e69b') ? 5 : 3

    const img = document.createElement('img')
    img.src = `https://t.thepennyhoarder.com/aff_lsr?offer_id=5706&transaction_id=${transactionId}&revenue=${payout}`
    img.style.display = 'none'
    const rootElement = document.getElementById('root')
    rootElement?.appendChild(img)
  }
}

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')

const processCreditCardOffers = (offers: CreditOffer[]) => (dispatch, getState) => {
  const state = getState()

  if (uiSelectedCreditStepSelector(state) === CreditCardStep.Preview) {
    dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.PreviewLoadedResults, offers.length, Product.CreditCard))
    dispatch({
      trackingData: { countOfOffers: offers.length },
      trackingKey: TrackingKey.CreditRateTablePreviewLoaded,
      type: ActionTypeKeys.CREDIT_RATE_TABLE_PREVIEW_LOADED,
    })
  } else {
    // Begin TPH CC Pixel Hack
    if (state.config.company_uuid === TPH_AMEX_COMPANY_UUID && offers.length > 0) {
      dispatch(tphPixelHackCreditCard(offers))
    }
    // End TPH CC Pixel Hack
    dispatch(
      trackGoogleAnalyticsEvent(GoogleAnalyticsActions.PersonalizedLoadedResults, offers.length, Product.CreditCard),
    )
    dispatch({
      trackingData: { countOfOffers: offers.length },
      trackingKey: TrackingKey.CreditRateTablePersonalizedLoaded,
      type: ActionTypeKeys.CREDIT_RATE_TABLE_PERSONALIZED_LOADED,
    })
    const numOffers: { [key in PersonalizedStatus]: number } = {
      [PersonalizedStatus.PreApproved]: 0,
      [PersonalizedStatus.PreQualified]: 0,
      [PersonalizedStatus.PreSelected]: 0,
    }
    offers.forEach((offer) => {
      PersonalizedStatuses.forEach((status) => {
        numOffers[status] += Number(!!get(offer, `details.${status}`))
      })
    })
    PersonalizedStatuses.forEach((status) => {
      if (numOffers[status]) {
        dispatch(
          trackGoogleAnalyticsEvent(
            GoogleAnalyticsActions[`RateTableContains${capitalize(status)}Offers`],
            numOffers[status],
            Product.CreditCard,
          ),
        )
        dispatch({
          trackingData: {
            [`countOf${capitalize(status)}Offers`]: numOffers[status],
          },
          trackingKey: TrackingKey[`CreditRateTable${capitalize(status)}Offers`],
          type: ActionTypeKeys[`CREDIT_RATE_TABLE_${snakeCase(status).toUpperCase()}_OFFERS`],
        })
      }
    })
    if (
      configCreditCardRewardsEnabledSelector(state) &&
      uiSelectedCreditStepSelector(state) === CreditCardStep.Results &&
      applicationCreditCardRewardsPreferenceValueSelector(state)
    ) {
      dispatch(fetchRewardValueCalculation())
    }
  }
}

// Fetch an individual offer by uuid
export const fetchOffer = (product: Product, offerUuid: string) => (
  dispatch: (arg0: FetchOfferSuccess | FetchOfferError) => void,
) => {
  Api.get(`/originator/offers/${offerUuid}`)
    .then((response) => dispatch(fetchOfferSuccess(product, response.data)))
    .catch((error) => {
      dispatch(fetchOfferError(product))
      captureGetStaticOfferError(product, offerUuid, error)
    })
}

export const collectClientTags = (state: CommonState): Partial<ClientTags> => {
  const rawQueryParamClientTags = QueryParams.getClientTags(state.config as ClientTags)

  // Set a client tag for each experiment the user is enrolled in currently
  // they client tag format is 'experiment-{{EXPERIMENT_ID}}': [{{SELECTED_VARIANT_ID}}]
  const experimentClientTags = {}
  Object.keys(state.experiments).forEach((experimentId) => {
    const experiment = state.experiments[experimentId]
    if (experiment.selectedVariantId !== undefined) {
      experimentClientTags[`experiment-${experimentId}`] = [experiment.selectedVariantId]
    }
  })

  // For the preferred look partner page, add client tags for the preferred offer partner's
  // company uuid and which layout the user is seeing
  const preferredOfferPartner = state.config[ConfigKeys.PreferredOfferPartner]
  const isPreferredLookPartnerPage = preferredOfferPartner !== undefined
  const preferredLookPartnerPageClientTags = isPreferredLookPartnerPage && {
    preferred_offer_partner: [preferredOfferPartner ?? ''],
    preferred_layout: [
      String(
        state.experiments[RedirectPreferredLook.id]?.selectedVariantId ===
          RedirectPreferredLook.variants.RedirectPreferredNewTemplate.id,
      ),
    ],
  }

  const clientTags: Partial<ClientTags> = {
    ...preferredLookPartnerPageClientTags,
    ...rawQueryParamClientTags,
    ...state.config.clientTags,
    ...experimentClientTags,
    consumerBrand: [state.config[ConfigServiceDataKeys.inPartnershipWithFiona] ? Brand.Fiona : brandFromHost ?? ''],
    isMobile: [Bowser.mobile ? 'true' : 'false'],
    mps_version: [releaseVersion ?? ''],
    os: [`${Bowser.osname} - ${Bowser.name} -  ${Bowser.version}`],
    partnerKey: [state.config[ConfigServiceDataKeys.partner] ?? ''],
    uiName: [state.config[ConfigServiceDataKeys.uiName] ?? ''],
  }
  if (state.parent[ParentDataKeys.Referrer]) {
    clientTags.evReferrer = [state.parent[ParentDataKeys.Referrer]]
  }
  if (state.config[ConfigKeys.PreferredOfferPartner]) {
    clientTags.preferred_offer_partner = [state.config[ConfigKeys.PreferredOfferPartner] ?? '']
  }
  return Object.keys(clientTags).reduce((acc, key) => {
    if (
      Array.isArray(clientTags[key]) && clientTags[key]?.[0]
        ? true
        : clientTags[key]?.[0] === null || clientTags[key]?.[0] === undefined
    ) {
      acc[key] = clientTags[key]
    }
    return acc
  }, {})
}

const getFieldValue = (state: CommonState, product: Product, field: Filter, fetchFromApplication?: boolean) => {
  if (fetchFromApplication) {
    return applicationStateSelector(state)[product]?.fields?.[field]?.value
  }

  return filtersProductSelector(state, product)?.[field]
}

function getBoolOrUndefined(value): boolean | undefined {
  if (value !== undefined) {
    return getBool(value)
  }
  return undefined
}

// eslint-disable-next-line complexity
const buildLead = async (state: CommonState, product: Product, fetchFromApplication?: boolean): Promise<Lead> => {
  const amountIntentRange: DepositAmountIntentRange | undefined = get(
    DEPOSITS_AMOUNT_INTENT_RANGE_FILTER_LIST.find(
      (el) => el.value === getFieldValue(state, product, Filter.DepositAmountIntentRange, fetchFromApplication),
    ),
    'data',
  )
  const amountIntentValue = state?.filters?.[Product.Deposits]?.[Filter.DepositAmountIntentValue]

  const amountIntent: DepositAmountIntentRange = amountIntentRange || {
    maxDepositAmount: amountIntentValue,
    minDepositAmount: amountIntentValue,
  }

  const isLifeInsuranceSimplifiedOffersVariant = !!state.experiments[LifeInsuranceSimplifiedOffers.id]
    ?.selectedVariantId

  const lead: Lead = {
    clientTags: collectClientTags(state),
    creditCardInformation: {
      [Filter.CardPurposes]: filtersCreditCardPurposesSelector(state)?.length
        ? [filtersCreditCardPurposesSelector(state)]
        : undefined,
    },
    creditInformation: {},
    educationInformation: {},
    financialInformation: {
      [Filter.AnnualIncome]: getIntFromString(getFieldValue(state, product, Filter.AnnualIncome, fetchFromApplication)),
      [Filter.EmploymentStatus]: getFieldValue(state, product, Filter.EmploymentStatus, fetchFromApplication),
      [Filter.PayFrequency]: getFieldValue(state, product, Filter.PayFrequency, fetchFromApplication),
    },
    healthInformation: {
      [Filter.Gender]: getFieldValue(state, product, Filter.Gender, fetchFromApplication),
      [Filter.HeightInInches]: getIntFromString(
        getFieldValue(state, product, Filter.HeightInInches, fetchFromApplication),
      ),
      [Filter.TobaccoSmoker]: getBoolOrUndefined(
        getFieldValue(state, product, Filter.TobaccoSmoker, fetchFromApplication),
      ),
      [Filter.WeightInPounds]: getIntFromString(
        getFieldValue(state, product, Filter.WeightInPounds, fetchFromApplication),
      ),
    },
    isTest: isPortalPreviewSelector(state),
    legalInformation: {
      [Filter.ConsentsToFCRA]: getBoolOrUndefined(
        getFieldValue(state, product, Filter.ConsentsToFCRA, fetchFromApplication),
      ),
      [Filter.ConsentsToTCPA]: getBoolOrUndefined(
        getFieldValue(state, product, Filter.ConsentsToTCPA, fetchFromApplication),
      ),
      // TCPALanguage is stored in filters (not application) and set by TCPALanguage component
      [Filter.TCPALanguage]: getFieldValue(state, product, Filter.TCPALanguage, fetchFromApplication),
    },
    loanInformation: {},
    mortgageInformation: {
      [Filter.PropertyStatus]: getFieldValue(state, product, Filter.PropertyStatus, fetchFromApplication),
    },
    personalInformation: {
      [Filter.ActiveMilitary]: getBoolOrUndefined(
        getFieldValue(state, product, Filter.ActiveMilitary, fetchFromApplication),
      ),
      [Filter.AddressOne]: getFieldValue(state, product, Filter.AddressOne, fetchFromApplication),
      [Filter.AddressTwo]: getFieldValue(state, product, Filter.AddressTwo, fetchFromApplication),
      [Filter.City]: getFieldValue(state, product, Filter.City, fetchFromApplication),
      [Filter.DateOfBirth]: getDateString(getFieldValue(state, product, Filter.DateOfBirth, fetchFromApplication)),
      [Filter.EducationLevel]: getFieldValue(state, product, Filter.EducationLevel, fetchFromApplication),
      [Filter.Email]: getFieldValue(state, product, Filter.Email, fetchFromApplication),
      [Filter.FirstName]: getFieldValue(state, product, Filter.FirstName, fetchFromApplication),
      [Filter.LastName]: getFieldValue(state, product, Filter.LastName, fetchFromApplication),
      [Filter.PrimaryPhone]: getFieldValue(state, product, Filter.PrimaryPhone, fetchFromApplication),
      [Filter.SocialSecurityNumber]: getFieldValue(state, product, Filter.SocialSecurityNumber, fetchFromApplication),
      [Filter.State]:
        getFieldValue(state, product, Filter.State, fetchFromApplication) !== undefined
          ? getFieldValue(state, product, Filter.State, fetchFromApplication).toUpperCase()
          : undefined,
      [Filter.Zipcode]: getFieldValue(state, product, Filter.Zipcode, fetchFromApplication),
    },
    productTypes: [ProductApiKeys[product]],
    savingsInformation: {
      [Filter.MinDepositAmount]: amountIntent ? amountIntent.minDepositAmount : undefined,
      [Filter.MaxDepositAmount]: amountIntent ? amountIntent.maxDepositAmount : undefined,
    },

    sessionInformation: {
      userAgent: navigator.userAgent,
    },
    sessionUuid: Session.sessionUuid,
    trackingUuid: Session.trackingUuid,
    vehicleInformation: {},
  }

  const isBasicLoan = LoanVariantEquals(loanVariantSelector(state), BasicLoan())
  const hasCoApplicant: boolean =
    getBool(getFieldValue(state, product, Filter.HasCoApplicant, fetchFromApplication)) && isBasicLoan
  if (hasCoApplicant) {
    lead[Filter.CoApplicantInformation] = {
      [Filter.AnnualIncome]: getIntFromString(
        getFieldValue(state, product, Filter.CoApplicantAnnualIncome, fetchFromApplication),
      ),
      [Filter.DateOfBirth]: getDateString(
        getFieldValue(state, product, Filter.CoApplicantDateOfBirth, fetchFromApplication),
      ),
      [Filter.FirstName]: getFieldValue(state, product, Filter.CoApplicantFirstName, fetchFromApplication),
      [Filter.LastName]: getFieldValue(state, product, Filter.CoApplicantLastName, fetchFromApplication),
    }
    if (
      getFieldValue(state, product, Filter.CoApplicantAddressMatch, fetchFromApplication) ===
      CoApplicantAddressMatch.SameAsApplicant
    ) {
      lead[Filter.CoApplicantInformation] = {
        ...lead[Filter.CoApplicantInformation],
        [Filter.City]: getFieldValue(state, product, Filter.City, fetchFromApplication),
        [Filter.State]:
          getFieldValue(state, product, Filter.State, fetchFromApplication) !== undefined
            ? getFieldValue(state, product, Filter.State, fetchFromApplication).toUpperCase()
            : undefined,
        [Filter.StreetAddressOne]: getFieldValue(state, product, Filter.AddressOne, fetchFromApplication),
        [Filter.StreetAddressTwo]: getFieldValue(state, product, Filter.AddressTwo, fetchFromApplication),
        [Filter.Zipcode]: getFieldValue(state, product, Filter.Zipcode, fetchFromApplication),
      }
    } else if (
      getFieldValue(state, product, Filter.CoApplicantAddressMatch, fetchFromApplication) ===
      CoApplicantAddressMatch.DifferentFromApplicant
    ) {
      lead[Filter.CoApplicantInformation] = {
        ...lead[Filter.CoApplicantInformation],
        [Filter.City]: getFieldValue(state, product, Filter.CoApplicantCity, fetchFromApplication),
        [Filter.State]:
          getFieldValue(state, product, Filter.CoApplicantState, fetchFromApplication) !== undefined
            ? getFieldValue(state, product, Filter.CoApplicantState, fetchFromApplication).toUpperCase()
            : undefined,
        [Filter.StreetAddressOne]: getFieldValue(state, product, Filter.CoApplicantAddressOne, fetchFromApplication),
        [Filter.StreetAddressTwo]: getFieldValue(state, product, Filter.CoApplicantAddressTwo, fetchFromApplication),
        [Filter.Zipcode]: getFieldValue(state, product, Filter.CoApplicantZipcode, fetchFromApplication),
      }
    }
  }

  // Add product-specific fields
  switch (product) {
    case Product.Loan:
      if (filtersLoanCreditScoreSelector(state)) {
        lead.creditInformation[Filter.CreditScore] = filtersLoanCreditScoreSelector(state)
      }
      if (getIntFromString(getFieldValue(state, product, Filter.LoanAmount, fetchFromApplication))) {
        lead.loanInformation[Filter.LoanAmount] = getIntFromString(
          getFieldValue(state, product, Filter.LoanAmount, fetchFromApplication),
        )
      }
      if (filtersLoanPurposeSelector(state)) {
        lead.loanInformation[Filter.LoanPurpose] = filtersLoanPurposeSelector(state)
      }
      if (filtersLoanPurposeSelector(state) === LoanPurposeOptions.STUDENT_REFINANCE) {
        lead.educationInformation[Filter.GraduationDate] = getDateStringMonthAndYearOnly(
          filtersProductSelector(state, product)?.[Filter.GraduationDate],
        )
        lead.educationInformation[Filter.GraduateDegreeType] = getFieldValue(
          state,
          product,
          Filter.GraduateDegreeType,
          fetchFromApplication,
        )
        lead.educationInformation[Filter.University] = getFieldValue(
          state,
          product,
          Filter.University,
          fetchFromApplication,
        )
        lead.educationInformation[Filter.EducationLevel] = getFieldValue(
          state,
          product,
          Filter.EducationLevel,
          fetchFromApplication,
        )
        lead.financialInformation[Filter.AvailableAssets] = getIntFromString(
          getFieldValue(state, product, Filter.AvailableAssets, fetchFromApplication),
        )
        lead.financialInformation[Filter.MonthlyHousingPayment] = getIntFromString(
          getFieldValue(state, product, Filter.MonthlyHousingPayment, fetchFromApplication),
        )
        lead.personalInformation[Filter.CitizenshipStatus] = getFieldValue(
          state,
          product,
          Filter.CitizenshipStatus,
          fetchFromApplication,
        )
      }
      if (
        filtersLoanPurposeSelector(state) === LoanPurposeOptions.AUTO_REFINANCE &&
        lead.vehicleInformation !== undefined
      ) {
        lead.financialInformation[Filter.MonthlyHousingPayment] = getIntFromString(
          getFieldValue(state, product, Filter.MonthlyHousingPayment, fetchFromApplication),
        )
        lead.vehicleInformation[Filter.MonthlyPayment] = getIntFromString(
          getFieldValue(state, product, Filter.MonthlyPayment, fetchFromApplication),
        )
        lead.vehicleInformation[Filter.EstimatedMileage] = getIntFromString(
          getFieldValue(state, product, Filter.EstimatedMileage, fetchFromApplication),
        )
        if (filtersVehicleIdTypeSelector(state) === VehicleIdTypeOptions.VEHICLE_IDENTIFICATION_NUMBER) {
          lead.vehicleInformation[Filter.VehicleIdentificationNumber] = getFieldValue(
            state,
            product,
            Filter.VehicleIdentificationNumber,
            fetchFromApplication,
          )
        } else if (filtersVehicleIdTypeSelector(state) === VehicleIdTypeOptions.LICENSE_PLATE) {
          lead.vehicleInformation[Filter.LicensePlateNumber] = getFieldValue(
            state,
            product,
            Filter.LicensePlateNumber,
            fetchFromApplication,
          )
          lead.vehicleInformation[Filter.LicensePlateState] = getFieldValue(
            state,
            product,
            Filter.LicensePlateState,
            fetchFromApplication,
          )
        }
      }
      // For loans we also want to send 'other' product type for special offers to show up correctly
      lead.productTypes?.push(ProductApiKeys[Product.Other])
      break
    case Product.CreditCard:
      // Send explicit empty list to overwrite mutable lead if none chosen
      if (!filtersCreditCardPurposesSelector(state)) {
        lead.creditCardInformation[Filter.CardPurposes] = []
      }

      lead.creditInformation[Filter.CreditScore] = filtersCreditCardCreditScoreSelector(state)
      // Add credit card flow to client tags
      lead.clientTags.credit_card_flow = [creditFlowSelector(state) || DefaultCreditCardFlow]
      break
    case Product.Deposits:
      // Don't send city or state for deposits since the user can't change them, we'll let the backend handle that
      lead.personalInformation[Filter.City] = undefined
      lead.personalInformation[Filter.State] = undefined
      break
    case Product.LifeInsurance:
      lead.lifeInsuranceInformation = {
        packageTypes: isLifeInsuranceSimplifiedOffersVariant
          ? [LifeInsuranceCoverageTier.Basic, LifeInsuranceCoverageTier.Essential, LifeInsuranceCoverageTier.Elite]
          : [LifeInsuranceCoverageTier.Essential],
      }
      break
    default:
  }

  if (isCreditCardDebtCompany(configCompanyUuidSelector(state))) {
    lead.financialInformation[Filter.CreditCardDebt] = getIntFromString(
      getFieldValue(state, product, Filter.CreditCardDebt, fetchFromApplication),
    )
  }

  try {
    const ip = await IpAddress.get()
    lead.sessionInformation.ipAddress = ip
    return lead
  } catch (e) {
    return lead
  }
}

export const buildLifeInsuranceResponses = (state: MultiProductState): LifeInsuranceAnswer[] =>
  getResponsePathWithSteps(
    applicationLifeInsuranceRouterQuestionsMachineConfigSelector(state),
    lifeInsuranceApplicationFieldsSelector(state),
  )
    .responsePath.filter((response) => response.currentState !== FinalStateNodeNames.Complete)
    .map((response) => ({
      questionUuid: getQuestionUuidFromQuestionMachineState(response.currentState),
      values: Array.isArray(response.value) ? response.value : [String(response.value)],
    }))

export interface RouterQuestionsResponse {
  questions: LifeInsuranceQuestion[]
  groups: LifeInsuranceQuestionGroups
}

export interface RedemptionTypesResponse {
  name: string
  uuid: string
}

export interface RedemptionTypesUuids {
  [key: string]: string
}

export const fetchRedemptionTypes = () => (dispatch) => {
  const url = '/offerAugmentation/redemptionTypes'
  dispatch(fetchRedemptionTypesStart())

  Api.get(url)
    .then((response: ApiResponse<RedemptionTypesResponse[]>) => {
      // TODO make Offer augmenter return enums
      const formattedResponse: RedemptionTypesUuids = response.data.reduce(
        (redemptionUuids: RedemptionTypesUuids, redemptionType: RedemptionTypesResponse) => {
          const typeFromLabel = mapRedemptionTypeFromLabelValue(redemptionType.name)
          if (typeFromLabel) {
            return {
              ...redemptionUuids,
              [typeFromLabel]: redemptionType.uuid,
            }
          }
          return redemptionUuids
        },
        {},
      )
      dispatch(filterChanged(Product.CreditCard, Filter.RedemptionTypes, formattedResponse))
      dispatch(fetchRedemptionTypesSuccess(Product.CreditCard, response))
    })
    .catch((error) => {
      dispatch(fetchRedemptionTypesError(Product.CreditCard, error?.response?.status))
      dispatch(filterChanged(Product.CreditCard, Filter.RedemptionTypes, DEFAULT_REDEMPTION_TYPE_VALUES))
    })
}

// TODO: Remove the getField helper once we migrate credit to the 'application' model
export const fetchRateTables = (product: Product, maxWaitMilliseconds = 28000, fetchFromApplication?: boolean) => (
  dispatch: MultiProductDispatch,
  getState: () => MultiProductState,
) => {
  const state = getState()

  dispatch(filterChanged(Product.Loan, Filter.LenderId, undefined))
  dispatch(fetchRateTablesStart(product))

  const isLifeInsurance = product === Product.LifeInsurance

  const fetchRateTablesRequest = (payload: Lead | { lead: Lead; lifeInsuranceResponses: LifeInsuranceAnswer[] }) => {
    const url = `${isLifeInsurance ? '/lifeInsurance' : ''}/leads/${Session.getLeadUuid()}/rateTables`

    Api.post(url, payload)
      .then((response) => dispatch(pollForRateTable(product, response, maxWaitMilliseconds)))
      .catch((error) => {
        captureGetRateTablesError(product, error)

        if (error.response) {
          dispatch(fetchRateTablesFailure(product, error.response.status))
        } else {
          dispatch(fetchRateTablesFailure(product))
        }
      })
  }

  buildLead(state, product, fetchFromApplication)
    .then((lead) => {
      if (isLifeInsurance) {
        fetchRateTablesRequest({ lead, lifeInsuranceResponses: buildLifeInsuranceResponses(state) })
      } else {
        fetchRateTablesRequest(lead)
      }
    })
    .catch(fetchRateTablesRequest)
}

export const fetchPreviewLoanOffers = () => (dispatch) => {
  dispatch(
    fetchPreviewOffers(Product.Loan, (commonLeadData: Lead) => ({
      [Filter.LoanAmount]: commonLeadData.loanInformation?.[Filter.LoanAmount],
      loanPurpose: commonLeadData.loanInformation?.[Filter.LoanPurpose],
      [Filter.CreditScore]: commonLeadData.creditInformation?.[Filter.CreditScore],
      zipCode: commonLeadData.personalInformation?.[Filter.Zipcode],
    })),
  )
}

export const fetchCreditCardOffers = () => (dispatch, getState) => {
  const state = getState()
  if (creditFlowSelector(state) === CreditCardFlow.Redirect) {
    dispatch(fetchRateTables(Product.CreditCard))
  } else {
    dispatch(fetchPreviewCreditCardOffers())
  }
}

const fetchPreviewCreditCardOffers = () => (dispatch) => {
  dispatch(
    fetchPreviewOffers(Product.CreditCard, (commonLeadData: Lead) => ({
      [Filter.CardPurposes]: commonLeadData.creditCardInformation?.[Filter.CardPurposes],
      [Filter.CreditScore]: commonLeadData.creditInformation?.[Filter.CreditScore],
    })),
  )
}

const fetchPreviewOffers = (product: Product, getRequestParams: (commonLeadData: Lead) => object) => (
  dispatch,
  getState,
) => {
  const state = getState()

  const fetchPreviewOffersRequest = (commonLeadData: Lead) => {
    const queryParams = urlSerialize(getRequestParams(commonLeadData))
    const url = `originator/preview/${ProductToApiProduct[product]}Offers?${queryParams}`

    dispatch(fetchPreviewOffersStart(product))
    Api.get(url)
      .then((response) => {
        const mappedResponse = transformPreviewOffersResponse(product, response)
        dispatch(processPreviewRateTableResponse(product, mappedResponse))
      })
      .catch((error) => {
        captureFetchPreviewOffersError(product, error)
        dispatch(fetchPreviewOffersFailure(product, error?.response?.status))
      })
  }

  buildLead(state, product).then(fetchPreviewOffersRequest).catch(fetchPreviewOffersRequest)
}

const transformPreviewOffersResponse = (
  product: Product,
  { data: offers, status, statusText, headers }: ApiResponse<Offer[]>,
) => ({
  data: {
    [`${ProductToApiProduct[product]}Offers`]: offers,
    requestTimestamp: new Date().getTime(),
  },
  headers,
  status,
  statusText,
})

export const fetchRateTablesFromApplication = (product: Product, maxWaitMilliseconds = 28000) =>
  fetchRateTables(product, maxWaitMilliseconds, true)

const getFilterReducerOptionsWithDefaults = (
  filters: Filters,
  options?: FieldsChangedOptions,
): FieldsChangedOptions => {
  const optionsWithDefaults = {}
  Object.keys(filters).forEach((filter) => {
    if (options === undefined || options[filter] === undefined) {
      optionsWithDefaults[filter] = { userInitiated: false }
    } else {
      optionsWithDefaults[filter] = {
        ...options[filter],
        userInitiated: options[filter].userInitiated === undefined ? false : options[filter].userInitiated,
      }
    }
  })
  return optionsWithDefaults
}
export const filtersChanged = (product: Product, filters: Filters, options?: FieldsChangedOptions) => (
  dispatch,
  getState,
) => {
  const state = getState()
  const validationData = validationDataSelector(state)

  // Default the userInitiated option to be explicitly false (vs undefined), unless passed to this action as true,
  // so that we can easily search CST events for both user-initiated and non-user-initiated changes
  const optionsWithDefaults = getFilterReducerOptionsWithDefaults(filters, options)

  dispatch({
    filters,
    options: optionsWithDefaults,
    product,
    trackingData: { ...filters, options: optionsWithDefaults },
    trackingKey: TrackingKey.FilterChange,
    type: ActionTypeKeys.FILTERS_CHANGED,
    validationData,
  })
}

export const filterChanged = (product: Product, filter: Filter, value, options?: FieldChangedOptions) => (
  dispatch,
  getState,
) => {
  let filterOption
  if (options !== undefined) {
    filterOption = { [filter]: options }
  }

  dispatch(filtersChanged(product, { [filter]: value }, filterOption))
}

export const nonApplicationFiltersChanged = (
  product: Product,
  filter: Filter,
  value,
  options?: FieldChangedOptions,
) => (dispatch, getState) => {
  let filterOption
  if (options !== undefined) {
    filterOption = { [filter]: options }
  }

  dispatch(nonApplicationFilterChanged(product, { [filter]: value }, filterOption))
}

export const nonApplicationFilterChanged = (product: Product, filters: Filters, options?: FieldsChangedOptions) => (
  dispatch,
  getState,
) => {
  const optionsWithDefaults = getFilterReducerOptionsWithDefaults(filters, options)
  const state = getState()
  const validationData = validationDataSelector(state)

  dispatch({
    filters,
    options: optionsWithDefaults,
    product,
    trackingData: { ...filters, options: optionsWithDefaults },
    trackingKey: TrackingKey.FilterChange,
    type: ActionTypeKeys.NON_APPLICATION_FILTER_CHANGED,
    validationData,
  })
}

export const filtersPopulated = (product: Product, filters: Filters, options?: FieldsChangedOptions) => (
  dispatch: (arg0: (product: Product, validFilters: unknown) => void) => void,
  getState,
) => {
  const state = getState()
  const validationData = validationDataSelector(state)

  // Make sure the filter values we're populating are valid before populating them
  const validFilters = Object.keys(filters).reduce((acc, key) => {
    const field = Object.values(Fields).find((el) => el.key === key)
    if (field?.validate(filters[key], validationData)) {
      acc[key] = filters[key]
    }
    return acc
  }, {}) as Filters

  if (Object.keys(validFilters).length > 0) {
    dispatch(filtersChanged(product, validFilters, options))
  }
}

// Note: the return type here is PromiseLike because the interface of the Promise class we return if an API call
// isn't necessary (from 'es6-promise') doesn't match the interface of the one Axios returns if we DO make an
// API call. es6-promise implements Promise.finally() and the Axios one doesn't.
export const fetchCityStateFromZip = (product: Product, zipcode: FieldDataValue) => (
  dispatch,
  getState,
): PromiseLike<unknown> => {
  const state = getState()

  // Check if the city or state has been entered in a filter or an application field.
  // We don't want to set them dynamically from the zipcode in that case.
  const getCityFromConfig = getFilterOrApp(state.config, Filter.City)
  const getStateFromConfig = getFilterOrApp(state.config, Filter.State)

  if (!getCityFromConfig && !getStateFromConfig) {
    return Geocoder.get({ components: `postal_code:${zipcode}` })
      .then((resp) => {
        resp.data.results.forEach((result) => {
          if (result.types.indexOf('postal_code') > -1) {
            let sublocality = ''
            let locality = ''
            let stateLocal = ''
            result.address_components.forEach((component) => {
              if (component.types.indexOf('sublocality') > -1) {
                sublocality = component.long_name
              } else if (component.types.indexOf('locality') > -1) {
                locality = component.long_name
              } else if (component.types.indexOf('administrative_area_level_1') > -1) {
                stateLocal = component.short_name
              }
            })

            dispatch(
              filtersChanged(
                product,
                {
                  [Filter.City]: sublocality || locality,
                  [Filter.State]: stateLocal,
                },
                {
                  [Filter.City]: { dataSource: FieldDataSources.GeoIP },
                  [Filter.State]: { dataSource: FieldDataSources.GeoIP },
                },
              ),
            )
          }
        })
      })
      .catch((error) => {
        captureGeocoderError({ address: zipcode }, error)
      })
  } else {
    return Promise.resolve()
  }
}

export const fetchStateFromIP = (product: Product) => (dispatch, getState) => {
  dispatch(calledGeocoder())
  getGeoFromIP()
    .then((response) => {
      dispatch(
        filtersPopulated(
          product,
          { [Filter.State]: response?.state },
          { [Filter.State]: { dataSource: FieldDataSources.GeoIP } },
        ),
      )
    })
    .catch((error) => {
      captureGeolocateError(product, error)
    })
}

export const fetchZipcodeFromIP = (product: Product, shouldFetchRateTables = true) => (dispatch, getState) => {
  dispatch(calledGeocoder())
  getGeoFromIP()
    .then((response) => {
      dispatch(
        filtersPopulated(
          product,
          {
            [Filter.City]: response?.city,
            [Filter.State]: response?.state,
            [Filter.Zipcode]: response?.zipcode,
          },
          {
            [Filter.City]: { dataSource: FieldDataSources.GeoIP },
            [Filter.State]: { dataSource: FieldDataSources.GeoIP },
            [Filter.Zipcode]: { dataSource: FieldDataSources.GeoIP },
          },
        ),
      )

      if (shouldFetchRateTables) {
        // this shouldn't be here
        switch (product) {
          // https://evenfinancial.atlassian.net/browse/FE-998
          case Product.Loan:
            dispatch(fetchPreviewLoanOffers())
            break
          default:
            dispatch(fetchRateTables(product))
        }
      }
    })
    .catch((error) => {
      captureGeolocateError(product, error)
    })
}

export const flowSelected = (product: Product, flow: ProductFlow) => ({
  flow,
  product,
  trackingData: {
    flow,
    product,
  },
  trackingKey: TrackingKey.FlowSelected,
  type: ActionTypeKeys.FLOW_SELECTED,
})

export interface RedirectOptions {
  openInNewTab?: boolean
  flow?: CreditCardFlow
}

// May want to add other fields in future
export function simplifyOffer(offer: Offer) {
  return { uuid: offer.uuid }
}

export const redirectToOffer = (
  offer: Offer,
  offerType: OfferType,
  redirectType: RedirectType,
  redirectOptions: RedirectOptions = { openInNewTab: false },
  // eslint-disable-next-line complexity
) => (dispatch: MultiProductDispatch, getState: () => MultiProductState) => {
  const getField = (field: Filter) => {
    if (offerType === OfferType.Special) {
      // Special offers don't line up with a product type
      return `application.${Product.Loan}.fields[${field}].value`
    } else {
      return `application.${offerType}.fields[${field}].value`
    }
  }
  const state = getState()

  const actionType =
    redirectType === RedirectType.AUTO ? ActionTypeKeys.OFFER_AUTO_REDIRECT : ActionTypeKeys.OFFER_MANUAL_REDIRECT
  const trackingKey = redirectType === RedirectType.AUTO ? TrackingKey.ResultRedirect : TrackingKey.ResultOfferClick
  const publishKey = redirectType === RedirectType.AUTO ? PublishKey.LoanOfferAutoRedirect : undefined

  let extraMetadata: LeadEventMetadata = {}

  switch (offerType) {
    case OfferType.CreditCard:
      extraMetadata = {
        isMobile: Bowser.mobile,
        os: Bowser.osname,
        recScore: (offer as CreditOffer).recommendationScore,
        uuid: Session.getLeadUuid(),
      }
      break
    case OfferType.Deposits:
      extraMetadata = {
        isMobile: Bowser.mobile,
        os: Bowser.osname,
        recScore: (offer as DepositsOffer).recommendationScore,
        uuid: Session.getLeadUuid(),
      }
      break
    case OfferType.LifeInsurance:
      extraMetadata = {
        isMobile: Bowser.mobile,
        os: Bowser.osname,
        recScore: (offer as LifeInsuranceOffer).recommendationScore,
        uuid: Session.getLeadUuid(),
      }
      if ('productSubType' in offer) {
        extraMetadata = { ...extraMetadata, selectedOfferSubType: (offer as LifeInsuranceOffer).productSubType }
      }

      break
    case OfferType.Loan:
    case OfferType.Special:
      extraMetadata = {
        credit_score: get(state, getField(Filter.CreditScore)),
        isMobile: Bowser.mobile,
        loan_amount: get(state, getField(Filter.LoanAmount)),
        os: Bowser.osname,
        purpose: get(state, getField(Filter.LoanPurpose)),
        recScore:
          offerType === OfferType.Special
            ? (offer as SpecialOffer).recommendationScore
            : (offer as LoanOffer).recommendationScore,
        uuid: Session.getLeadUuid(),
      }
      break
    default:
  }

  const action = {
    offer,
    publishKey,
    trackingData: {
      offer: simplifyOffer(offer),
      offerType,
    },
    trackingKey,
    type: actionType,
  }

  // Dispatch publish event
  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.ProductClicked))
  switch (offerType) {
    case OfferType.CreditCard:
      dispatch(trackGoogleAnalyticsPageview(state.ui.selectedProduct, CreditCardStep.RedirectToOffer))
      dispatch(creditCardOfferClick(extraMetadata))
      break
    case OfferType.Deposits:
      dispatch(depositsOfferClick(extraMetadata))
      break
    case OfferType.Loan:
      dispatch(trackGoogleAnalyticsPageview(state.ui.selectedProduct, LoanStep.RedirectToOffer))
      dispatch(offerClick(extraMetadata))
      break
    case OfferType.LifeInsurance:
      dispatch(trackGoogleAnalyticsPageview(state.ui.selectedProduct, LifeInsuranceStep.RedirectToOffer))
      dispatch(lifeInsuranceOfferClick(extraMetadata))
      break
    case OfferType.Special:
      dispatch(trackGoogleAnalyticsPageview(state.ui.selectedProduct, LoanStep.RedirectToOffer))
      dispatch(specialOfferClick(extraMetadata))
      break
    default:
      dispatch(offerClick(extraMetadata))
  }

  dispatch(action)

  // Remove when reporting doesn't depend on this
  dispatch({
    trackingData: {
      offer: simplifyOffer(offer),
      offerType,
    },
    trackingKey: RedirectType.AUTO ? TrackingKey.ResultRedirectOLD : TrackingKey.ResultOfferClickOLD,
    type: 'OFFER_REDIRECT_OLD', // Do not depend on this
  })
  // Remove above when reporting doesn't depend on this

  // Terrible hack required to make TPH not remove us - figure out a way to make this a first class feature soon
  const tphCompanyUuids = new Set([
    '80186feb-216c-45f2-9e75-01b893512495',
    'd766c7fd-7961-4b9b-8cb0-f2f3725a5f1c',
    'a86adf41-48b1-4d08-a928-b468f1eb30a6',
  ])
  const tphHackOfferTypes = new Set([OfferType.Loan, OfferType.Special])
  let redirectUrl: string
  if (tphHackOfferTypes.has(offerType) && tphCompanyUuids.has(Session.companyUuid)) {
    const cookieKey = `_subid_${Session.companyUuid}`
    let transactionId = ''
    if (Cookies.get(cookieKey)) {
      transactionId = Cookies.get(cookieKey) ?? ''
    } else {
      transactionId = get(state, 'config.clientTags.subid.0')
      Cookies.set(cookieKey, transactionId, { expires: 31536000000, sameSite: 'none', secure: true })
    }
    let payout
    switch (get(state, getField(Filter.CreditScore))) {
      case 'excellent':
        payout = 13.0
        break
      case 'good':
        payout = 9.0
        break
      case 'fair':
        payout = 5.5
        break
      case 'poor':
        payout = 3.0
        break
      default:
    }
    const trackingUrl = encodeURIComponent(
      `http://t.thepennyhoarder.com/aff_lsr?offer_id=4156&transaction_id=${transactionId}&revenue=${payout}`,
    )
    redirectUrl = `${offer.url}?trackingUrl=${trackingUrl}`
  } else if (configCompanyUuidSelector(state) === TPH_LIFE_INSURANCE_COMPANY_UUID) {
    // This type assertion is safe since we're checking a life insurance company uuid above
    const trackingUrl = encodeURIComponent(getTphPixelHackLifeInsuranceUrl(offer as LifeInsuranceOffer, state))
    redirectUrl = `${offer.url}?trackingUrl=${trackingUrl}`
  } else {
    redirectUrl = offer.url
  }
  window.setTimeout(() => {
    Parent.requestRedirect(redirectUrl, redirectOptions.openInNewTab ? TargetTypes.NEW_TAB : TargetTypes.CURRENT_WINDOW)
  }, REDIRECT_WAIT)
}

export const loaded = (): Loaded => ({
  trackingKey: TrackingKey.Loaded,
  type: ActionTypeKeys.LOADED,
})

export const loadConfigBegin = (): LoadConfigBegin => ({
  type: ActionTypeKeys.LOAD_CONFIG_BEGIN,
})

export const loadConfigSuccess = (config: Config): LoadConfigSuccess => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const { 'theme.partnerLogo': excludedProp, ...omitedConfig } = config

  return {
    config,
    trackingData: omitedConfig,
    trackingKey: TrackingKey.PageLoad,
    type: ActionTypeKeys.LOAD_CONFIG_SUCCESS,
  }
}

export const loadParam = (param: QueryParamKeys, value: unknown): LoadParam => ({
  param,
  type: ActionTypeKeys.LOAD_PARAM,
  value,
})

export const loadParentData = (data: ParentData): LoadParentData => ({
  data,
  trackingData: data,
  trackingKey: TrackingKey.LoadParentData,
  type: ActionTypeKeys.LOAD_PARENT_DATA,
})

export const setMaxResults = (product: Product, max: number): SetMaxResults => ({
  max,
  product,
  type: ActionTypeKeys.SET_MAX_RESULTS,
})

export const stepSelected = (product: Product, step: ProductStep, isSteppingBack = false) => (dispatch) => {
  dispatch(trackGoogleAnalyticsPageview(product, step))
  dispatch({
    product,
    publishKey: PublishKey.StepSelected,
    step,
    trackingData: {
      product,
      step,
    },
    trackingKey: TrackingKey.StepSelected,
    type: ActionTypeKeys.STEP_SELECTED,
    isSteppingBack,
  })
}

export const stepBack = (product: Product) => (dispatch: CommonDispatch, getState: () => MultiProductState) => {
  const previousStep = uiSelectedStepHistorySelector(getState(), product).slice(-1)[0]
  dispatch(stepSelected(product, previousStep, true))
}

export const applicationStepSelected = (product: Product, applicationStep: ApplicationStep) => (dispatch) => {
  dispatch({
    applicationStep,
    product,
    publishKey: PublishKey.ApplicationStepSelected,
    trackingData: {
      applicationStep,
      product,
    },
    trackingKey: TrackingKey.ApplicationStepSelected,
    type: ActionTypeKeys.APPLICATION_STEP_SELECTED,
  })
}

export const depositsCalculatorInputChanged = (amount: number) => (dispatch) => {
  dispatch(filterChanged(Product.Deposits, Filter.DepositAmountIntentValue, amount))

  // Update the lead now that their deposit intent has changed
  debouncedUpdateLead(dispatch, Product.Deposits)

  // Fire GA event
  trackGoogleAnalyticsEvent(GoogleAnalyticsActions.DepositsCalculatorChanged, amount)

  // Fire CST event
  dispatch({
    trackingData: { amount },
    trackingKey: TrackingKey.DepositsCalculatorChanged,
    type: ActionTypeKeys.CST_EVENT,
  })
}

export const depositsCalculatorSliderChanged = (amount: number) => (dispatch) => {
  dispatch(filterChanged(Product.Deposits, Filter.DepositAmountIntentValue, amount))

  // Update the lead now that their deposit intent has changed
  debouncedUpdateLead(dispatch, Product.Deposits)

  // Fire GA event
  trackGoogleAnalyticsEvent(GoogleAnalyticsActions.DepositsCalculatorSliderChanged, amount)

  // Fire CST event
  dispatch({
    trackingData: { amount },
    trackingKey: TrackingKey.DepositsCalculatorSliderChanged,
    type: ActionTypeKeys.CST_EVENT,
  })
}

export const depositsCalculatorLoadedResults = (offerCount: number) => (dispatch) => {
  trackGoogleAnalyticsEvent(GoogleAnalyticsActions.DepositsCalculatorLoadedResults, offerCount)

  // Fire CST event
  dispatch({
    trackingData: { offerCount },
    trackingKey: TrackingKey.DepositsCalculatorLoadedResults,
    type: ActionTypeKeys.CST_EVENT,
  })
}

export const depositsCalculatorOfferClicked = (offer: DepositsOffer) => (dispatch, getState) => {
  const state = getState()
  const amount = state?.filters?.[Product.Deposits]?.[Filter.DepositAmountIntentValue]

  // Fire GA
  trackGoogleAnalyticsEvent(GoogleAnalyticsActions.DepositsCalculatorOfferClicked, amount)

  // Fire CST event
  dispatch({
    trackingData: { amount },
    trackingKey: TrackingKey.DepositsCalculatorOfferClicked,
    type: ActionTypeKeys.CST_EVENT,
  })

  dispatch(
    redirectToOffer(offer, OfferType.Deposits, RedirectType.MANUAL, {
      openInNewTab: isFixedSizeEmbed(),
    }),
  )
}

const redirectPreferredLook = (offer: LoanOffer) => (dispatch, getState) => {
  const state: MultiProductState = getState()
  const redirectPreferredLookVariant = state.experiments[RedirectPreferredLook.id]?.selectedVariantId

  const partnerPageQueryParams = {
    loan_flow: LoanFlow.StepbystepApplication,
    step: LoanStep.Application,
  }
  const partnerPageParams: [Filters, ClientTags, string?] = [
    state.filters.loan as Filters,
    {
      ...state.config.clientTags,
      ...(offer.originator?.companyUuid && { preferred_offer_partner: [offer.originator.companyUuid] }),
      preferred_layout: [
        String(redirectPreferredLookVariant === RedirectPreferredLook.variants.RedirectPreferredNewTemplate.id),
      ],
    },
    'fiona-loans',
  ]

  switch (redirectPreferredLookVariant) {
    case RedirectPreferredLook.variants.Redirect.id: {
      const partnerPageUrl = buildConsumerPartnerPageUrl(Product.Loan, partnerPageQueryParams, ...partnerPageParams)
      Parent.requestRedirect(partnerPageUrl, TargetTypes.NEW_TAB)
      break
    }
    case RedirectPreferredLook.variants.RedirectPreferred.id: {
      const partnerPageUrl = buildConsumerPartnerPageUrl(
        Product.Loan,
        { ...partnerPageQueryParams, preferred_offer_partner: offer.originator?.companyUuid },
        ...partnerPageParams,
      )
      Parent.requestRedirect(partnerPageUrl, TargetTypes.NEW_TAB)
      break
    }
    case RedirectPreferredLook.variants.RedirectPreferredNewTemplate.id: {
      const partnerPageUrl = buildConsumerPartnerPageUrl(
        'Preferred',
        { ...partnerPageQueryParams, preferred_offer_partner: offer.originator?.companyUuid },
        ...partnerPageParams,
      )
      Parent.requestRedirect(partnerPageUrl, TargetTypes.NEW_TAB)
      break
    }
    default: {
      // The "original" behavior for this experiment is the native application flow.
      dispatch(stepSelected(Product.Loan, LoanStep.Application))
      dispatch({
        offer,
        trackingData: {
          offer: simplifyOffer(offer),
        },
        trackingKey: TrackingKey.PreviewOfferClick,
        type: ActionTypeKeys.LOAN_OFFER_PREVIEW_SELECTED,
      })

      // Remove when reporting doesn't depend on this
      dispatch({
        trackingData: { offer: simplifyOffer(offer) },
        trackingKey: TrackingKey.PreviewOfferClickOLD,
        type: 'LOAN_OFFER_PREVIEW_SELECTED_OLD', // Don't depend on this
      })
    }
  }
}

export const loanOfferPreviewSelected = (offer: LoanOffer) => (dispatch, getState) => {
  const state: MultiProductState = getState()
  const flow = get(state, 'config.loan_flow')

  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.ProductClicked))

  const redirectPreferredLookActivated = state.experiments[RedirectPreferredLook.id]?.activated

  // If we're in the Redirect Preferred Look experiment, override the loan flow behavior with one of
  // the experiment behaviors, and return early. If not, proceed with the normal loan flow behavior.
  if (redirectPreferredLookActivated) {
    dispatch(redirectPreferredLook(offer))
    return
  }

  switch (flow) {
    case LoanFlow.Redirect:
      // If we're not the application flow, link them to the partner page with the application step selected
      const partnerPageUrl = buildConsumerPartnerPageUrl(
        Product.Loan,
        {
          [QueryParamKeys.LoanFlow]: LoanFlow.Native,
          [QueryParamKeys.Step]: LoanStep.Application,
        },
        get(getState(), `filters.${Product.Loan}`),
        get(getState(), `config.${ConfigServiceDataKeys.clientTags}`),
        get(state, `config.${ConfigServiceDataKeys.partner}`),
      )
      Parent.requestRedirect(partnerPageUrl, TargetTypes.NEW_TAB)
      break
    case LoanFlow.Native:
    default:
      // If we're in the application flow, take them to the application set directly without linking out to another page
      dispatch(stepSelected(Product.Loan, LoanStep.Application))
      dispatch({
        offer,
        trackingData: {
          offer: simplifyOffer(offer),
        },
        trackingKey: TrackingKey.PreviewOfferClick,
        type: ActionTypeKeys.LOAN_OFFER_PREVIEW_SELECTED,
      })

      // Remove when reporting doesn't depend on this
      dispatch({
        trackingData: { offer: simplifyOffer(offer) },
        trackingKey: TrackingKey.PreviewOfferClickOLD,
        type: 'LOAN_OFFER_PREVIEW_SELECTED_OLD', // Don't depend on this
      })
  }
}

export const creditCardResultsOfferSelected = (offer: CreditOffer) => (dispatch) => {
  dispatch(
    trackGoogleAnalyticsEvent(
      GoogleAnalyticsActions.ResultsOfferClicked,
      undefined,
      Product.CreditCard,
      offer.details.cardName,
    ),
  )
  dispatch({
    trackingData: { offer: simplifyOffer(offer) },
    trackingKey: TrackingKey.CreditResultsOfferClicked,
    type: ActionTypeKeys.CREDIT_RESULTS_OFFER_CLICKED,
  })
}

export const creditCardOfferPreviewSelected = (
  offer: CreditOffer,
  redirectOptions: RedirectOptions = { openInNewTab: false },
) => (dispatch, getState) => {
  const stateFlow = creditFlowSelector(getState())
  const flow = redirectOptions.flow || stateFlow
  const supportsPreSelect = get(offer, 'partner.supportsPreSelect') === true
  const supportsPreQual = get(offer, 'partner.supportsPersonalizedOffers') === true

  dispatch(
    trackGoogleAnalyticsEvent(
      GoogleAnalyticsActions.PreviewOfferClicked,
      undefined,
      Product.CreditCard,
      offer.details.cardName,
    ),
  )
  dispatch({
    trackingData: { offer: simplifyOffer(offer) },
    trackingKey: TrackingKey.CreditPreviewOfferClicked,
    type: ActionTypeKeys.CREDIT_PREVIEW_OFFER_CLICKED,
  })

  switch (flow) {
    case CreditCardFlow.PreSelect:
      if (supportsPreSelect || supportsPreQual) {
        if (supportsPreSelect) {
          // If they support preSelect, take them to the pre-select form
          dispatch(stepSelected(Product.CreditCard, CreditCardStep.PreSelectForm))
        } else {
          dispatch(stepSelected(Product.CreditCard, CreditCardStep.PreQualifiedForm))
        }
        dispatch({
          offer,
          trackingData: {
            offer: simplifyOffer(offer),
          },
          trackingKey: TrackingKey.PreviewOfferClick,
          type: ActionTypeKeys.CREDIT_OFFER_PREVIEW_SELECTED,
        })
      } else {
        // If they don't support pre-select, redirect them to the offer
        dispatch(redirectToOffer(offer, OfferType.CreditCard, RedirectType.MANUAL, redirectOptions))
      }
      break
    case CreditCardFlow.Redirect:
    default: {
      if (supportsPreQual) {
        // If they support preQual, take them to the pre-qual form
        dispatch(stepSelected(Product.CreditCard, CreditCardStep.PreQualifiedForm))
        dispatch({
          offer,
          trackingData: {
            offer: simplifyOffer(offer),
          },
          trackingKey: TrackingKey.PreviewOfferClick,
          type: ActionTypeKeys.CREDIT_OFFER_PREVIEW_SELECTED,
        })
      } else {
        // If they don't support pre-qual, redirect them to the offer
        dispatch(redirectToOffer(offer, OfferType.CreditCard, RedirectType.MANUAL, redirectOptions))
      }
    }
  }
}

export const seeAllDepositsSelected = () => (_, getState) => {
  const state = getState()

  const partnerPageUrl = buildConsumerPartnerPageUrl(
    Product.Deposits,
    {
      [QueryParamKeys.Step]: DepositsStep.SeeMore,
    },
    get(state, `filters.${Product.Deposits}`),
    get(getState(), `config.${ConfigServiceDataKeys.clientTags}`),
    get(state, `config.${ConfigServiceDataKeys.partner}`),
  )

  Parent.requestRedirect(partnerPageUrl, TargetTypes.NEW_TAB)
}

export const seeAllLoansSelected = () => (_, getState) => {
  const state = getState()

  const partnerPageUrl = buildConsumerPartnerPageUrl(
    Product.Loan,
    {},
    get(state, `filters.${Product.Loan}`),
    get(getState(), `config.${ConfigServiceDataKeys.clientTags}`),
    get(state, `config.${ConfigServiceDataKeys.partner}`),
  )
  Parent.requestRedirect(partnerPageUrl, TargetTypes.NEW_TAB)
}

export const seeMoreFromLender = (product: Product, lenderId: string, lenderName: string) => (dispatch) => {
  dispatch(filterChanged(product, Filter.LenderId, lenderId, { lenderName }))
  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.SeeMoreFromLender, undefined, undefined, lenderName))
  dispatch({
    trackingData: {
      lenderId,
      lenderName,
    },
    trackingKey: TrackingKey.SeeMoreFromLender,
    type: ActionTypeKeys.SEE_MORE_FROM_LENDER,
  })
}

export const clearSorts = (product: Product): SortCleared => ({
  product,
  type: ActionTypeKeys.SORT_CLEARED,
})

export const sortChanged = (direction: SortDirection, product: Product, sort: SortOption) => (dispatch) => {
  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.SortChanged, undefined, undefined, sort))
  dispatch(sortChangedAction(direction, product, sort))
}

export const debouncedUpdateLead = debounce(500, (dispatch, product: Product) => dispatch(updateLead(product)))

export const updateLead = (product: Product) => async (
  _: CommonDispatch,
  getState: () => CommonState,
): Promise<void | ApiResponse> => {
  const state = getState()
  if (state.config.company_uuid === BUSINESS_INSIDER_COMPANY_UUID) {
    // unless loanAmount is present, don't patch the lead
    if (!state.application.loan?.fields?.loanAmount?.isValid) {
      return
    }
  }
  return buildLead(state, product, true)
    .then((lead) => Api.patch(buildPatchLeadUrl(product), buildPatchLeadData(getState(), product, lead)))
    .catch((error) => {
      captureLeadApiError(product, Session.getLeadUuid(), error)
      throw error
    })
}

export const updateTags = (product: Product) => async (
  _: CommonDispatch,
  getState: () => CommonState,
): Promise<void | ApiResponse> => {
  const state = getState()
  if (state.config.company_uuid === BUSINESS_INSIDER_COMPANY_UUID) {
    // unless loanAmount is present, don't patch the lead
    if (!state.application.loan?.fields?.loanAmount?.isValid) {
      return
    }
  }
  return Promise.resolve(collectClientTags(state))
    .then((clientTags) =>
      Api.patch(buildPatchLeadUrl(product), buildPatchLeadData(getState(), product, { clientTags })),
    )
    .catch((error) => {
      captureLeadApiError(product, Session.getLeadUuid(), error)
      throw error
    })
}

export const fetchRouterQuestions = () => (dispatch: MultiProductDispatch) => {
  dispatch(fetchRouterQuestionsStart())

  Api.get('/lifeInsurance/question')
    .then(({ data }: ApiResponse<RouterQuestionsResponse>) => {
      dispatch(fetchRouterQuestionsSuccess(data))
    })
    .catch((error) => {
      dispatch(fetchRouterQuestionsError(error?.response?.status))
    })
}

export const buildPatchLeadUrl = (product: Product) =>
  `${product === Product.LifeInsurance ? '/lifeInsurance' : ''}/leads/${Session.getLeadUuid()}`

export const buildPatchLeadData = (state: CommonState, product: Product, lead: Partial<Lead>) => {
  const leadUuid = Session.getLeadUuid()
  if (product === Product.LifeInsurance) {
    // When we have TCPA consent, then we also want to add the quoteId to the request payload because the
    // life insurance service needs to pass it on to LeapLife
    // When patching the lead, we don't post to LeapLife, so we don't need the router question responses
    const selectedOffer = (lead.legalInformation as LeadLegalInformation).consentsToTcpa
      ? lifeInsuranceOfferSelectedSelector(state)
      : undefined
    const rateTableUuid = lifeInsuranceRatesUuidSelector(state)
    if (selectedOffer) {
      const selectedOfferUrl = selectedOffer.url
      const quoteId = selectedOffer.details.partnerQuoteId
      const rateTableOfferUuid = selectedOfferUrl.substring(selectedOfferUrl.lastIndexOf('/') + 1)
      return { lead, lifeInsuranceResponses: [], leadUuid, quoteId, rateTableUuid, rateTableOfferUuid }
    }
    return { lead, lifeInsuranceResponses: [], leadUuid, ...(rateTableUuid ? { rateTableUuid } : {}) }
  }
  return lead
}

export const createAccount = (password: string) => async (
  dispatch: MultiProductDispatch,
  getState: () => MultiProductState,
) => {
  const state = getState()
  const email = loanEmailSelector(state)
  const rateTableUuid = loanRatesUuidSelector(state)
  const leadUuid = loanRatesLeadUuidSelector(state)
  const firstName = loanApplicationFieldsSelector(state)?.firstName?.value

  const data = {
    email,
    firstName,
    leadUuid,
    password,
    rateTable: rateTableUuid,
  }
  try {
    const res = await fetch(`${fionaBaseApiUrl}/api/users`, {
      body: JSON.stringify(data),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json;charset=UTF-8',
      },
      method: 'POST',
    })
    const response = { data: await res.json() }
    if (response.data.error) {
      return `Password ${response.data.error?.details?.message}`
    } else {
      dispatch(createAccountSuccess())
    }
  } catch (error) {
    return error?.response?.data?.error?.message ?? 'Sorry, there was an error creating your account'
  }
}
